import { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { isRenewalReadOnlyField } from '@/lib/renewalReadOnlyFields';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import type { AnyTFunction } from '@/i18n/msg';
import { formatList } from '@/i18n/intl';
import type { FamilyApplication } from '@/types/applications';
import type { ApplicationDocumentMeta, AddressCandidate, AddressValidationData, AddressVerificationStatus } from '@/types/applications';
import {
  alignCountryAnswers,
  repickPreviousLabel,
  type CountryRepick,
  resolveFieldLabel,
  isFieldValueEmpty,
  hasAnythingToPick,
  fieldValueProblem,
  get,
  setPath,
  type ApplicationScreen,
  type FieldSpec,
} from '@/lib/intakeFields';
import type { FieldType, SectionKey } from '@/lib/intakeFields';
import {
  fieldIndex,
  screenSections,
  sectionFieldsFrom,
  toApplicationScreens,
  driverIndex,
  isRevealed,
  type DriverIndex,
  type SectionAnswers,
  type FormDefinitionVersion,
} from '@/lib/formDefinitions';
import { dateRuleProblems, rulesFromFields, SEEDED_DATE_RULES } from '@/lib/dateRules';
import { DefinitionGate, useFormDefinition, type FormLocalization } from '@/components/apply/FormDefinitionGate';
import { ConsentEnglishNotice, FormLanguageNotice, ShownInEnglish } from '@/components/apply/FormLanguageNotice';
import type { PendingDecisionId } from '@/lib/pending-decisions';
import {
  fieldValueProblemRequest,
  localizeDateRules,
  resolveValidationMessage,
  type ValidationRequest,
} from '@/components/apply/validationMessages';
import { useLocale } from '@/i18n/useLocale';
import { IntakeFieldInput } from '@/components/intake/IntakeFieldInput';
import { DateDraftContext, type DateDraftRegistry } from '@/components/intake/dateDrafts';
import { DisclosureGate } from '@/components/consent/DisclosureGate';
import { DocumentUploadStep, type DocSlot, type UploadOptions, type UploadOutcome } from '@/components/apply/DocumentUploadStep';
import { uploadFailureText } from '@/lib/uploadTypes';
import { markRequiredSlots, missingRequiredSlots, useRequiredDocumentKinds } from '@/components/apply/requiredDocuments';
import { isCanadaIntakeClosedError, useCanadaIntakeClosed } from '@/components/apply/canadaIntakeGate';
import { CanadaIntakeClosedNotice } from '@/components/apply/CanadaIntakeClosedNotice';
import { ReviewSummary } from '@/components/apply/ReviewSummary';
import { WIZARD_STEP_CONTAINER } from '@/components/apply/wizardLayout';
import { AddressReviewDialog } from '@/components/apply/AddressReviewDialog';
import { WizardProgress } from '@/components/apply/WizardProgress';
import { MonoLabel } from '@/components/editorial';
import { useCopy, type CopyBundle } from '@/hooks/useCopy';
import { DOCUMENT_KIND_SLOT_CAPS, openAuthedDocument } from '@/lib/documents';
import { ToastAction } from '@/components/ui/toast';
import { DOCUMENT_KIND_LABEL_KEY } from '@/lib/applications-display';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { applicationsDataSource, syncMineCache, type WizardDataSource } from '@/lib/wizardDataSource';
import { renewalYearNumber } from '@/lib/renewal-display';
import { DOCUMENTS_STEP_KEY, firstFlaggedStep } from '@/lib/flagged-fields';
import { documentKindLabel } from '@/lib/documents';
import { reportClientError } from '@/lib/reportClientError';
import { TRF_CONTACT_EMAIL, TRF_CONTACT_PHONE } from '@/lib/trfContact';
import { api, APPLY_INVITE_STORAGE_KEY } from '@/services/api';
import { ApplicationCodeEntry } from '@/components/apply/InviteRequiredStop';
import { ExternalLink } from '@/components/ui/ExternalLink';

/**
 * #2752: on the renewal flow, the renewal variant of a family-facing string —
 * but only when the reader's language HAS that variant. The variants ship in
 * English first and the other catalogs carry `""` until the translate pass;
 * with `returnEmptyString: false` an empty target renders the English
 * fallback, so a Spanish family would swap "Cargando su solicitud…" for
 * English mid-page. Until their renewal line lands they keep their own
 * language's application wording.
 */
type WordingI18n = { language?: string; resolvedLanguage?: string; getResource: (lng: string, ns: string, key: string) => unknown };
function hasOwnWording(i18n: WordingI18n, nsKey: string): boolean {
  const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  if (lng === 'en' || lng.startsWith('en-')) return true;
  const colon = nsKey.indexOf(':');
  const value = i18n.getResource(lng, nsKey.slice(0, colon), nsKey.slice(colon + 1));
  return typeof value === 'string' && value.trim() !== '';
}
function wordingFor<A extends string, R extends string>(
  i18n: WordingI18n, flow: 'initial' | 'renewal', applicationKey: A, renewalKey: R,
): A | R {
  return flow === 'renewal' && hasOwnWording(i18n, renewalKey) ? renewalKey : applicationKey;
}

/**
 * #2752 option (a) (Victor 2026-10-13): two strings the renewal walk shows
 * come from the PUBLISHED definition, and one published version serves /apply
 * and /renew — the builder has no flow-conditional variant. So the renewal
 * wording for these two is code-owned, keyed by the same stage slot the
 * definition's own translation uses (`screen.<id>.title`,
 * `field.<section>.<id>.label`). The /apply walk keeps the published string,
 * and follows it when staff edit it in the builder; these overrides do not.
 */
const RENEWAL_WORDING = {
  'screen.region_cycle.title': 'forms:wizard.renewal.regionCycleTitle',
  'field.household.secondary_contact.authorized.label': 'forms:wizard.renewal.secondaryContactAuthorizedLabel',
} as const;

/**
 * `[slot, line]` for each renewal slot whose line exists in `lng`, the
 * language the FORM renders in (not the UI's): a form shown in English to a
 * Spanish reader, because it is not offered in es, reads its renewal line in
 * English. No line in that language, or no catalog loaded: the published
 * string stays.
 */
function renewalLinesIn(i18n: WordingI18n, lng: string): Array<[string, string]> {
  return Object.entries(RENEWAL_WORDING).flatMap(([slot, key]): Array<[string, string]> => {
    const own = i18n.getResource(lng, 'forms', key.slice('forms:'.length));
    return typeof own === 'string' && own.trim() !== '' ? [[slot, own]] : [];
  });
}

/**
 * The version the renewal walk renders: the slots above replaced by their
 * renewal line. `wording` holds only the slots whose renewal line exists in
 * the language the FORM renders in, so a published translation is never
 * swapped for English mid-page — the #2841 rule, applied to the form's own
 * language because these are form strings.
 */
function withRenewalWording(version: FormDefinitionVersion, wording: ReadonlyMap<string, string>): FormDefinitionVersion {
  if (wording.size === 0) return version;
  return {
    ...version,
    screens: version.screens.map((screen) => {
      const title = wording.get(`screen.${screen.id}.title`);
      return title ? { ...screen, title } : screen;
    }),
    fields: version.fields.map((field) => {
      const label = wording.get(`field.${field.section_id}.${field.id}.label`);
      return label ? { ...field, label } : field;
    }),
  };
}

// ── Step definitions ──────────────────────────────────────────────────────────
// 2027 workbook match: the wizard walks the workbook's own SCREEN structure
// (APPLICATION_SCREENS, intakeFields.ts) while storage stays on the five
// Family-parity sections — a screen may span sections, and advancing saves each
// touched section. PHI consent sits BEFORE the first patient-data screen,
// exactly where it sat before the patient section previously.

type StepKind =
  | { type: 'screen'; screen: ApplicationScreen }
  | { type: 'consent'; documentId: string; label: string; eyebrow: string; title: string; explanation: string; consentLabel: string; optional?: boolean }
  | { type: 'documents'; label: string }
  | { type: 'submit_consent'; label: string };

// ── The code-pinned steps (D-FB-9) ────────────────────────────────────────────
// Screens are builder DATA — TRF adds, reorders, and retires them. These four
// are NOT: the PHI authorization is legally pinned before the first screen that
// asks for patient data, and the publicity release, the document upload, and
// review/submit are code-composed flows rather than question lists. #2800: the
// family-facing grant ask is gone — TRF gives every selected family the fixed
// Signature grant, so there is nothing for a family to request.
// Lifting that skeleton is a recorded follow-up, not this PR's business.

/**
 * #1009 / AP-26: the staff-editable chrome of the two code-pinned consent
 * steps, resolved through `copy()` in the OUTER component so the definition
 * gate can hold paint on the copy read too (Guardrail 7). The consent
 * `explanation`/`consentLabel` sentences below are deliberately NOT slots —
 * consent wording belongs to the versioned legal documents (AP-26's own
 * exclusion). The PHI/PIPEDA sentences are additionally unreachable:
 * `findLegalPhrase` (copyLocks.js) refuses their phrases store-wide. The
 * publicity pair carries no barred phrase, so staying unregistered is the
 * only thing keeping it out of a copy edit's reach.
 */
/** The chrome the two code-pinned consent STEPS are built from (#1009).
 *
 *  Split out from `WizardChrome` by #1636: the step builders below are pure
 *  functions of these four strings, and the copy editor's D-CS-12 preview
 *  builds exactly this to drive the real gate. Widening what they demand every
 *  time the wizard resolves another slot would make every such preview a
 *  compile error for strings it never renders. */
export interface ConsentStepChrome {
  phiGateEyebrow: string;
  phiGateTitle: string;
  publicityEyebrow: string;
  publicityTitle: string;
}

/** Everything the wizard itself resolves from the copy store. */
export interface WizardChrome extends ConsentStepChrome {
  /** #1636: what the health-information step says while it does not yet know —
   *  or could not find out — which document this family should be shown. Copy
   *  slots, not consent copy: they say a page did not load, never what it
   *  would have asked (see the block on these slots in `lib/copySlots.ts`). */
  regionLoadingEyebrow: string;
  regionLoadingBody: string;
  regionErrorEyebrow: string;
  regionErrorTitle: string;
  regionErrorBody: string;
  regionErrorRetry: string;
  regionErrorToastBody: string;
  /** #2109 R4-B29: the code-pinned steps' names, in the reader's language. */
  stepLabels: StepLabels;
}

export type ConsentStep = Extract<StepKind, { type: 'consent' }>;

/** The code-pinned steps' names on the progress bar (#2109 R4-B29). Resolved
 *  by the caller; the default reads the active language, so the copy editor's
 *  preview and the placement tests need not pass them. */
export interface StepLabels { health: string; documents: string; review: string }

type LooseT = (key: string, options?: Record<string, unknown>) => string;

// eslint-disable-next-line react-refresh/only-export-components -- see phiConsentStep
export function stepLabelsFrom(t: AnyTFunction = i18n.t): StepLabels {
  const tt = t as unknown as LooseT;
  return {
    health: tt('forms:wizard.steps.health'),
    documents: tt('forms:wizard.steps.documents'),
    review: tt('forms:wizard.steps.review'),
  };
}

/** Exported for the copy editor's preview (D-CS-12): the preview renders the
 *  REAL gate with these exact sentences, so it can never drift from them.
 *  The disables below: a non-component export from a component file trips
 *  react-refresh, but these builders must live BESIDE the wizard so the
 *  copySlots.verify surface checks read one file (CI caps lint warnings; see
 *  community-panel-ids.ts). */
// eslint-disable-next-line react-refresh/only-export-components
export const phiConsentStep = (chrome: ConsentStepChrome, label: string = stepLabelsFrom().health): ConsentStep => ({
  type: 'consent',
  documentId: 'phi_authorization',
  label,
  eyebrow: chrome.phiGateEyebrow,
  title: chrome.phiGateTitle,
  // i18next-instrument-ignore — PHI consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language
  explanation:
    'The next sections ask about your child’s medical history and cancer treatment. ' +
    'Sharing this helps TRF understand your family’s situation. ' +
    'This information is protected health information (PHI) and will only be used to process your application.',
  // i18next-instrument-ignore-next-line — PHI consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language
  consentLabel: 'I authorize TRF to collect and use my child’s protected health information for this application.',
  optional: false,
});

// eslint-disable-next-line react-refresh/only-export-components -- see phiConsentStep
export const publicityConsentStep = (chrome: ConsentStepChrome): ConsentStep => ({
  type: 'consent',
  documentId: 'publicity_release',
  // The progress bar has always named this step with the same words as its
  // on-screen headline — one slot serves both so they cannot drift apart.
  label: chrome.publicityTitle,
  eyebrow: chrome.publicityEyebrow,
  title: chrome.publicityTitle,
  // i18next-instrument-ignore — publicity release consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language
  explanation:
    'TRF occasionally shares family photos and stories with donors and on social media. ' +
    'This is entirely optional — declining will not affect your application.',
  // i18next-instrument-ignore-next-line — publicity release consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language
  consentLabel: 'I’m comfortable with TRF sharing our family’s photos and story.',
  optional: true,
});

const tailSteps = (chrome: ConsentStepChrome, labels: StepLabels): StepKind[] => [
  publicityConsentStep(chrome),
  { type: 'documents', label: labels.documents },
  { type: 'submit_consent', label: labels.review },
];

/**
 * Weave the definition's screens together with the code-pinned steps.
 *
 * The PHI gate is placed by RULE, not by index: it goes immediately before the
 * first screen that touches the `patient` section. Pinning it to a position
 * would silently put it in the wrong place the first time TRF reorders screens,
 * and "the consent came after we asked" is not a bug you want to find later.
 * A definition with no patient screen at all still gets the gate, ahead of the
 * tail steps, rather than losing it.
 */
export function buildSteps(screens: ApplicationScreen[], chrome: ConsentStepChrome, labels: StepLabels = stepLabelsFrom()): StepKind[] {
  const steps: StepKind[] = [];
  let phiPlaced = false;
  for (const screen of screens) {
    if (!phiPlaced && screenSections(screen).includes('patient')) {
      steps.push(phiConsentStep(chrome, labels.health));
      phiPlaced = true;
    }
    steps.push({ type: 'screen', screen });
  }
  if (!phiPlaced) steps.push(phiConsentStep(chrome, labels.health));
  return [...steps, ...tailSteps(chrome, labels)];
}

/** A step's stable id: the screen id, the consent document id, or the step
 *  type. It is what the progress bar keys on and what the resume pointer
 *  stores (#2440). */
function stepId(s: StepKind): string {
  return s.type === 'screen' ? s.screen.id : s.type === 'consent' ? s.documentId : s.type;
}

/** #2440: the server's shape check for a resume pointer
 *  (FamilyApplication.RESUME_STEP_PATTERN). A step id outside it is never
 *  sent, so a builder-minted id the server would refuse costs no round trip. */
const RESUME_STEP_ID = /^[a-z0-9_.-]{1,64}$/;

/**
 * #2948 (d): the server lists documents newest-first
 * (SourceDocument.findByApplication sorts `created_at` descending), while an
 * upload in this tab appends at the end. Loaded oldest-first, the family's
 * photos keep their upload order across a reload instead of flipping. Rows
 * without a time keep the server's order, reversed.
 */
function oldestFirst(docs: ApplicationDocumentMeta[]): ApplicationDocumentMeta[] {
  const reversed = [...docs].reverse();
  if (!reversed.every((d) => typeof d.created_at === 'string')) return reversed;
  return reversed
    .map((d, i) => ({ d, i, t: Date.parse(d.created_at as string) }))
    .sort((a, b) => (a.t - b.t) || (a.i - b.i))
    .map(({ d }) => d);
}

/** #2527: each step's flag keys, in walk order — `<section>.<field>` for a
 *  screen's questions, `documents` for the upload step — so a needs-more-info
 *  request can be placed on the steps that hold what TRF asked about. */
function flagKeysByStep(steps: StepKind[]): string[][] {
  return steps.map((s) =>
    s.type === 'screen'
      ? s.screen.groups.flatMap((g) => g.fields.map((ref) => `${ref.section}.${ref.key}`))
      : s.type === 'documents' ? [DOCUMENTS_STEP_KEY] : []);
}

/**
 * #2527: the top of every step while TRF waits on the family. The renewal
 * used to open with no sign TRF had asked for anything; only the email said
 * so. Names each asked-about question or document as a button to its step
 * (the phone has no step list), and quotes the staff message as TRF wrote it.
 */
function MoreInfoBanner({
  message,
  items,
  onOpen,
  english,
}: {
  message: string | null;
  items: Array<{ key: string; label: string; step: number }>;
  onOpen: (step: number) => void;
  english: boolean;
}) {
  const { t } = useTranslation('forms');
  return (
    <section
      className="mb-6 space-y-2 rounded-lg border border-status-warn-border bg-status-warn p-4 text-body text-status-warn-fg"
      data-testid="more-info-banner"
      aria-label={t('wizard.moreInfo.title')}
    >
      <p className="m-0 font-medium">{t('wizard.moreInfo.title')}</p>
      <p className="m-0">{t('wizard.moreInfo.body')}</p>
      {message && (
        // #2736: a pasted link (one long unbroken word) ran out of the banner
        // and over the step list — `overflow-wrap:anywhere` breaks it inside
        // the card. #2734: the note keeps the line breaks TRF typed, as the
        // email does, so a bulleted list stays a list.
        <p className="m-0 [overflow-wrap:anywhere]" data-testid="more-info-banner-message">
          {t('wizard.moreInfo.attribution')}{' '}
          <span className="italic whitespace-pre-line" lang="en">“{message}”</span>
          {!english && <span className="block text-body-sm">{t('wizard.moreInfo.shownInEnglish')}</span>}
        </p>
      )}
      {items.length > 0 && (
        <div>
          <p className="m-0">{t('wizard.moreInfo.askedLabel')}</p>
          <ul className="m-0 list-disc space-y-1 ps-5">
            {items.map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => onOpen(item.step)}
                  className="rounded-sm text-start underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function toWizardSteps(steps: StepKind[]) {
  return steps.map((s) => ({
    id: stepId(s),
    label: s.type === 'screen' ? s.screen.label : s.label,
  }));
}

// #329/D-7 (issue #329): PIPEDA-framed copy for the CA branch of the health-
// information consent step — a structural mirror of the phi_authorization
// step's copy above, reframed for Canadian applicants. Dark by default: it
// only renders when country==='CA' AND a PUBLISHED pipeda_authorization
// document exists (checked below via the public /api/legal index, which
// excludes drafts — see backend/src/data/legal/index.js). Every other case
// (US, unset country, or CA while the doc is still draft) renders the exact
// same phi_authorization step as before this feature shipped — D-7's hard
// acceptance criterion #1 (byte-identical US behavior).
//
// #1009: only the LEGAL sentences live here now. The eyebrow and title read
// the phi step's chrome slots at the render site — the eyebrow as-is, the
// title with a code-pinned " (Canada)" suffix — so a staff edit follows both
// branches and the unedited render keeps D-7's exact wording.
/**
 * #1636: what the wizard knows about the Canadian consent document.
 *
 *   idle        — not a CA applicant (or the country just changed); nothing asked.
 *   loading     — the index request is in flight.
 *   published   — a PUBLISHED pipeda_authorization exists → the CA arm.
 *   unpublished — the index answered and does not carry it → today's behaviour,
 *                 the US-framed phi_authorization step, unchanged.
 *   error       — the index could not be read. NOT the same as 'unpublished',
 *                 which is the entire point: only a document the server actually
 *                 answered for may decide what a family is asked to sign.
 */
type PipedaLookup = 'idle' | 'loading' | 'published' | 'unpublished' | 'error';

// i18next-instrument-ignore — PIPEDA consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language, pinned byte-identical by D-7 (#329)
const PIPEDA_CONSENT_COPY = {
  explanation:
    'The next sections ask about your child’s medical history and cancer treatment. ' +
    'Sharing this helps TRF understand your family’s situation. ' +
    'This is personal health information protected under Canadian privacy law (PIPEDA and ' +
    'applicable provincial health-information legislation) and will only be used to process ' +
    'your application.',
  consentLabel:
    'I authorize TRF to collect and use my child’s personal health information for this ' +
    'application, in accordance with PIPEDA.',
};

// `bill_statement`, `child_photo` and `family_photo` are true multi-slot (F-8 /
// F-040 / #2472): bill statements up to 5, each photo slot up to 3, all
// persisted independently. Every other kind
// (diagnosis_letter, income_verification) is single-slot; replacing one there
// goes through DocumentUploadStep's confirm-before-replace guard (F-2).
// `maxCount` from the cleansix build-defaults memo (OQ7); the photo caps are
// #2472's "1 required, up to 3".
// Labels come from the shared kind→label keys (#540, #2109 R4-B29); the
// bill_statement slot names itself in the plural (a count key) because the
// slot accepts multiple uploads.
/** What a family reads when an upload failed and the server said nothing useful.
 *  Declared once because both upload paths (the code-pinned document-slots step and
 *  D-FB-12's per-field control) mean the same thing by it. */
const UPLOAD_FAILED_FALLBACK_KEY = 'forms:wizard.uploadFailed';

/** #2198: the server's refusal code for a submit after the cycle's deadline
 *  (backend/src/services/cycleSubmissionGuard.js `CYCLE_CLOSED_CODE`). */
const CYCLE_CLOSED_CODE = 'APPLICATION_CYCLE_CLOSED';
/** #2197: the submit refusal for an empty mandatory document slot
 *  (backend/src/helpers/requiredApplicationDocuments.js). */
const REQUIRED_DOCUMENTS_MISSING_CODE = 'REQUIRED_DOCUMENTS_MISSING';
/** #2943: the submit refusal for a blank required answer
 *  (backend/src/services/submitChecks.js `assertRequiredAnswers`). */
const REQUIRED_ANSWERS_MISSING_CODE = 'REQUIRED_ANSWERS_MISSING';
/** #2819: the create refusal when this tab carries no usable pre-screen
 *  answers (backend/src/routes/applications.js `screenOrRefuse`). */
const PRESCREEN_REQUIRED_CODE = 'PRESCREEN_REQUIRED';
/** #2850: the renewal create refusal for a parent with no admitted family
 *  (backend/src/routes/renewals.js `POST /`). */
const RENEWAL_NOT_ELIGIBLE_CODE = 'RENEWAL_NOT_ELIGIBLE';
/** #2687: the renewal create refusal past Year 3 — renewals stop there
 *  (backend/src/routes/renewals.js `POST /`). */
const RENEWAL_YEAR_CAP_CODE = 'RENEWAL_YEAR_CAP';
/** #2792 follow-up: the create's eligibility MISS refusal
 *  (backend/src/routes/applications.js `screenOrRefuse`). Every other 403 on
 *  the create (a referral or code refusal, a closed cycle) carries no code. */
const PRESCREEN_MISS_CODE = 'PRESCREEN_MISS';
/** Wave launch-w1014 L3-a: the create's credential refusal — no usable
 *  invite or code reached the server (backend/src/routes/applications.js
 *  `REFERRAL_REQUIRED_CODE`). */
const REFERRAL_REQUIRED_CODE = 'APPLICATION_REFERRAL_REQUIRED';
/** #3452: the create refusal for a new family before the cycle's "New families
 *  from" day (backend/src/routes/applications.js `NEW_FAMILIES_NOT_OPEN_CODE`). */
const NEW_FAMILIES_NOT_OPEN_CODE = 'APPLICATION_NEW_FAMILIES_NOT_OPEN';

/** #3452: the day new families may apply from, as the refusal names it (YYYY-MM-DD), or undefined. */
function newFamiliesOpenOnOf(err: unknown): string | undefined {
  const day = (err as { response?: { data?: { details?: { new_families_open_on?: unknown } } } })
    ?.response?.data?.details?.new_families_open_on;
  return typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined;
}

/** Wave launch-w1014 L3-a: why the create refused, for the on-page stop.
 *  `referral` and `cycle-closed` are the two coded refusals; anything else
 *  (an uncoded 403, a 5xx, a network failure) is `other`. */
type StartRefusalReason = 'referral' | 'cycle-closed' | 'new-families' | 'other';
function startRefusalReason(err: unknown): StartRefusalReason {
  const code = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  if (code === REFERRAL_REQUIRED_CODE) return 'referral';
  if (code === CYCLE_CLOSED_CODE) return 'cycle-closed';
  if (code === NEW_FAMILIES_NOT_OPEN_CODE) return 'new-families';
  return 'other';
}

/** #2819: a 422 the family fixes by answering the pre-screen — and only that
 *  one. A miss is a 403 and never matches, so it cannot route round the stop. */
function isPrescreenRequired(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return res?.status === 422 && res.data?.code === PRESCREEN_REQUIRED_CODE;
}

/** #2792 follow-up: the create refused on an eligibility miss — and only that
 *  403. A refresh can never fix that. */
function isPrescreenMiss(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return res?.status === 403 && res.data?.code === PRESCREEN_MISS_CODE;
}

/** #2850: a 403 that says this account has no grant to renew — and only
 *  that one. Any other 403 keeps the refresh-and-retry toast. */
function isRenewalNotEligible(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return res?.status === 403 && res.data?.code === RENEWAL_NOT_ELIGIBLE_CODE;
}

/** #2687: a 403 that says the family's grant is in its final year. */
function isRenewalYearCap(err: unknown): boolean {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } })?.response;
  return res?.status === 403 && res.data?.code === RENEWAL_YEAR_CAP_CODE;
}

/** The server's own reason, for an English reader only (#2109 R3-B11): it is
 *  English on the server, so every other reader gets the platform's line. */
function englishServerMessage(err: unknown, language: string): string | undefined {
  if (!(language === 'en' || language.startsWith('en-'))) return undefined;
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message || undefined;
}

// #1425: how many a slot holds is ONE fact and it is not restated here. The
// tile's `multiple`/`maxCount` are SPREAD from DOCUMENT_KIND_SLOT_CAPS
// (lib/documents.ts), which is pinned key-for-key to the backend's map of the
// same name by backend/tests/contract/document-slot-cap-parity.test.js. Hand-
// writing them here is what let `income_verification` be a single slot in this
// list and uncapped on the server, so the family's confirmed Replace of a
// paystub was accepted and superseded nothing.
const capacity = (kind: keyof typeof DOCUMENT_KIND_SLOT_CAPS) =>
  (DOCUMENT_KIND_SLOT_CAPS[kind] === 1
    ? {}
    : { multiple: true, maxCount: DOCUMENT_KIND_SLOT_CAPS[kind] });

// #1009: the HINTS are registered copy slots; the labels stay on the shared
// kind→label map, which staff surfaces render too and is therefore not
// registrable (D-CS-1's ownership rule). Built from `copy()` in the outer
// component — behind the definition gate's ready-hold — and exported so the
// copy editor's preview renders the exact tiles a family gets (D-CS-12).
// eslint-disable-next-line react-refresh/only-export-components -- see phiConsentStep
export const buildDocSlots = (copy: CopyBundle['copy'], t: AnyTFunction = i18n.t): DocSlot[] => {
  const tt = t as unknown as LooseT;
  return [
    { kind: 'diagnosis_letter', label: tt(DOCUMENT_KIND_LABEL_KEY.diagnosis_letter), hint: copy('apply.documents.hint_diagnosis_letter'), phi: true, ...capacity('diagnosis_letter') },
    { kind: 'income_verification', label: tt(DOCUMENT_KIND_LABEL_KEY.income_verification), hint: copy('apply.documents.hint_income_verification'), phi: true, ...capacity('income_verification') },
    // #2109 R4-B20: the multi-upload slot names itself with a count key (its
    // cap), so each language can pluralize it. English keeps "Bill
    // statement(s)" byte-identical (DECISIONS 2026-09-30 salvage).
    { kind: 'bill_statement', label: tt('forms:documents.billStatements', { count: DOCUMENT_KIND_SLOT_CAPS.bill_statement }), hint: copy('apply.documents.hint_bill_statement'), ...capacity('bill_statement') },
    // #2472 (D10): the child's photo and the family's are two slots, each
    // with its own drop zone, roster and requirement.
    { kind: 'child_photo', label: tt('forms:documents.childPhoto'), hint: copy('apply.documents.hint_child_photo'), ...capacity('child_photo') },
    { kind: 'family_photo', label: tt('forms:documents.familyPhoto'), hint: copy('apply.documents.hint_family_photo'), ...capacity('family_photo') },
  ];
};

// ── #1059 address validation ──────────────────────────────────────────────────
// The contact screen's address group is validated on advance, BEFORE the save.
// Suggest, never block: every outcome either advances the family or returns
// them to editing at their own request.

type AddressChoice = 'use_suggested' | 'keep_as_entered' | 'edit';

type AddressOutcome =
  | { action: 'stay' }
  | { action: 'proceed'; status: AddressVerificationStatus | null; suggested?: AddressCandidate };

// ── Helpers ───────────────────────────────────────────────────────────────────

// B-162: these field types commit their value on click, not blur — a clicked
// pill/checkbox only blurs on a LATER interaction, so without a click-time save
// a selection made right before a reload is silently lost.
const CLICK_COMMIT_TYPES: ReadonlySet<FieldType> = new Set(['multi', 'select', 'checkbox']);

/**
 * #2727: how long typing has to pause before the wizard saves it. Blur was the
 * only save for typed answers, so the LAST field a family typed into was lost
 * whenever the tab closed without a blur — a phone killing the tab, a closed
 * browser — while the sidebar said "Saved ✓ · just now". Short enough that a
 * closed tab loses a second of typing at most; long enough not to save on
 * every keystroke.
 */
export const AUTOSAVE_DELAY_MS = 1200;

const ALL_SECTIONS: readonly SectionKey[] = ['intake', 'patient', 'household', 'finances', 'travel'];

type SaveTarget = SectionKey;

/** #2800: the retired family-facing grant step's id. Drafts saved before the
 *  step went may still carry it as `resume_step` (and a stored `grant_request`
 *  section); both resume on the step that came after it. */
const LEGACY_GRANT_STEP_ID = 'grant_request';
const LEGACY_GRANT_STEP_SUCCESSOR = 'publicity_release';

function hasLegacyGrantAnswers(app: FamilyApplication): boolean {
  const legacy = app.grant_request as Record<string, unknown> | undefined | null;
  return !!legacy && typeof legacy === 'object' && Object.keys(legacy).length > 0;
}

/**
 * #2399 (D-2399 ships the lists; these are the drafted details): the `(?)`
 * a Canadian family's questions still carry, on the question it is about.
 * The answer lists, Blue Cross and protected-leave wording were ruled as
 * built on 2026-10-16; the question wording and the bands stay open for TRF.
 */
const CANADA_FIELD_DECISIONS: Partial<Record<string, PendingDecisionId>> = {
  insurance_coverage: 'canada-question-wording',
  // #2523: where the Canadian bands draw their lines.
  income_band: 'canada-band-thresholds',
  spend_band: 'canada-band-thresholds',
  miles_band: 'canada-band-thresholds',
};

function seedSection(app: FamilyApplication, key: SectionKey): Record<string, unknown> {
  return (app[key] as Record<string, unknown>) ?? {};
}

/** #2448: the pre-screen prefill entries whose patient field is still empty
 *  (and still asked by the published form). Never overwrites an answer. */
export function prefillEmpty(
  stored: Record<string, unknown>,
  prefill: Record<string, string> | undefined,
  asked: (key: string) => boolean,
): [string, string][] {
  if (!prefill) return [];
  return Object.entries(prefill).filter(([key, v]) => {
    const cur = stored[key];
    const empty = cur == null || (typeof cur === 'string' && cur.trim() === '');
    return empty && typeof v === 'string' && v.trim() !== '' && asked(key);
  });
}

/** B-DRAFT-1: where to resume a returning draft — the last step the applicant
 *  actually put data into (a filled screen or an upload), so they don't
 *  re-click through completed steps. Never lands PAST an unvisited consent gate:
 *  consent steps carry no app data, so they're never the resume target, and any
 *  gate reached on "continue" self-resolves (auto-skips if already accepted).
 *  A screen counts as filled when ANY of its fields holds a value in the
 *  hydrated application (screens span sections, so section-level emptiness is
 *  no longer the right test).
 *
 *  B-403 (`guardIncomplete`): for a PRE-FILLED draft, never land PAST a screen
 *  whose required fields are still empty. "Any field filled" is not "screen
 *  finished", and forward validation only ever checks the step on screen — so a
 *  screen skipped here was never checked by anything.
 *
 *  Why this is opt-in rather than always-on: a draft filled in wizard order
 *  cannot reach the bad state, because advancing past a screen already requires
 *  satisfying it — so for `/apply` the guard would only ever fire on states a
 *  real applicant cannot produce, while changing where many existing drafts
 *  resume. A RENEWAL draft is the reachable case: `createRenewal` pre-fills from
 *  the Family record, which populates `patient` while leaving `intake` empty, so
 *  resume jumped to the patient screen and `residence_state` (conditional on
 *  `intake.country`) rendered permanently disabled three steps later — submit
 *  blocked, no route back. Guarded, resume takes the EARLIER of "furthest data"
 *  and "first unsatisfied required step".
 *
 *  If `/apply` ever gains out-of-order pre-fill, turn this on there too.
 *
 *  PR-4: `STEPS` and `findFieldSpec` are parameters, not module state — both
 *  come from the published definition the renderer walks, so the guard judges
 *  "required" by the exact spec the field will render with.
 *
 *  #1057 (`hasDocuments`): an upload IS data the applicant put in, so the
 *  documents step counts as filled exactly like a screen.
 *  Uploads live on their own collection rather than on `app`, so the caller
 *  passes the answer — and must FETCH the list before calling, which is the
 *  other half of the bug: the list used to arrive after the pointer was set,
 *  so a family who uploaded and closed resumed two steps back and re-walked
 *  the publicity gate and the upload step every time. Consent steps are
 *  deliberately still not counted: they hold no data, and an accepted one
 *  self-resolves on arrival (DisclosureGate). The documents step sits AFTER
 *  the publicity gate, but resuming ON it lands on no gate at all — and it is
 *  only reachable by having walked that gate once already. */
function computeResumeIndex(
  app: FamilyApplication,
  STEPS: StepKind[],
  findFieldSpec: (section: SectionKey | string, key: string) => FieldSpec | undefined,
  guardIncomplete = false,
  hasDocuments = false,
  drivers: DriverIndex = new Map(),
): number {
  const valueAt = (ref: { section: SectionKey; key: string }): unknown => {
    const sectionData = (app[ref.section] as Record<string, unknown>) ?? {};
    return get(sectionData, ref.key);
  };
  const hasValue = (v: unknown) =>
    v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);

  // #1639: the pointer DEMANDS only questions this family is actually being
  // asked — the same rule the renderer and the step gate use, one evaluator
  // across all three. Without it a hidden required field is a "gap" that
  // re-parks a returning family on a screen where the question it is waiting
  // for is not on the page.
  //
  // It deliberately does NOT narrow what the pointer COUNTS (`filled` below).
  // A stored answer is data the family put in and therefore evidence it reached
  // that screen, whether or not the question holding it is visible right now —
  // the same principle #1057 applied to uploads. Filtering it would send a
  // family who answered a renewal-only question and then switched to "new
  // applicant" back to screen one, which is the exact re-walk #1057 and #1219
  // were fixing. Hiding is not clearing (D-FB-11), so it must not be forgetting
  // either. A screen that ends up with nothing left to ask is handled where it
  // belongs — the empty-screen walk-through below — not by refusing to count it.
  const answers: SectionAnswers = {
    intake: app.intake, patient: app.patient, household: app.household,
    finances: app.finances, travel: app.travel,
  };
  const asked = (ref: { section: SectionKey; key: string }): boolean =>
    isRevealed(findFieldSpec(ref.section, ref.key)?.visibleWhen, drivers, answers);

  let furthestWithData = 0;
  let firstIncomplete = STEPS.length - 1;
  let foundIncomplete = false;

  STEPS.forEach((s, i) => {
    const filled =
      (s.type === 'screen' && s.screen.groups.some((g) => g.fields.some((ref) => hasValue(valueAt(ref)))))
      || (s.type === 'documents' && hasDocuments)
      // #2800: a legacy draft that filled the retired grant step reached the
      // tail; its first remaining step (the publicity release) stands in.
      || (s.type === 'consent' && s.documentId === LEGACY_GRANT_STEP_SUCCESSOR && hasLegacyGrantAnswers(app));
    if (filled) furthestWithData = i;

    // A screen is incomplete when any field the wizard would REQUIRE is empty.
    // Consent/document/submit steps carry no such fields and never qualify.
    if (guardIncomplete && !foundIncomplete && s.type === 'screen') {
      const incomplete = s.screen.groups.some((g) =>
        g.fields.some((ref) => {
          const spec = findFieldSpec(ref.section, ref.key);
          if (spec?.required !== true || hasValue(valueAt(ref))) return false;
          // #1639: a question this family was never asked is not a gap it left.
          if (!asked(ref)) return false;
          // #1219: the same rule the advance gate applies, spelled the same
          // way. An empty field whose options resolved to nothing is not a
          // gap the family left — clamping resume to it re-parks them on a
          // screen they cannot finish, every single time they come back.
          return hasAnythingToPick(
            spec,
            (app[ref.section] as Record<string, unknown>) ?? {},
            (key) => findFieldSpec(ref.section, key),
          );
        }),
      );
      if (incomplete) {
        firstIncomplete = i;
        foundIncomplete = true;
      }
    }
  });

  // #2440: the step the family was ON, when the draft names one this walk
  // still has. "Furthest data" was only ever a guess at it — a family on step
  // 5 that had typed nothing there yet came back on step 4, and a renewal
  // family (pre-filled out of order) came back on step 1. The guess remains
  // the fallback for drafts saved before the pointer existed, and the B-403
  // clamp below wins over either.
  //
  // #2800: a draft saved ON the retired grant step names an id this walk no
  // longer has. It resumes on the step that followed it — the publicity
  // release — rather than falling back to the "furthest data" guess, which
  // would send the family back to the last screen.
  const resumeId = app.resume_step === LEGACY_GRANT_STEP_ID ? LEGACY_GRANT_STEP_SUCCESSOR : app.resume_step;
  const pointer = typeof resumeId === 'string' ? STEPS.findIndex((s) => stepId(s) === resumeId) : -1;
  const target = pointer >= 0 ? pointer : furthestWithData;

  return Math.min(target, firstIncomplete);
}

function useTimedSave() {
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markSaved = useCallback(() => {
    setSavedAt(new Date());
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setSavedAt(null), 8_000);
  }, []);

  return { savedAt, markSaved };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface ApplicationWizardProps {
  /** Which lifecycle this wizard drives. Defaults to the initial /apply flow. */
  flow?: 'initial' | 'renewal';
  /** Which API the wizard reads/writes through. Defaults to the applications API. */
  dataSource?: WizardDataSource;
}

/**
 * PR-4: the wizard's questions come from the published `application`
 * definition, not from `intakeFields.ts`. The fetch is gated OUTSIDE the
 * component body so every hook below stays unconditional, and so a failed
 * definition load can never render a half-built form — AP-58 / the B-400
 * MoneySummary lesson: a surface that cannot load its questions says so.
 */
export function ApplicationWizard(props: ApplicationWizardProps = {}) {
  // #2752: the renewal walk's load-failure line says renewal.
  const state = useFormDefinition(api.forms.publishedApplication, props.flow === 'renewal' ? 'renewal' : 'application');
  // #1009: the consent-step chrome and the document-tile hints resolve through
  // the copy store. Guardrail 7: the gate below holds at the skeleton until the
  // copy read has SETTLED too, so a seed never flickers into an override.
  //
  // LATCHED to the first settle, never re-armed: after an API-down settle the
  // errored query is permanently stale, so any LATER consumer mounting its own
  // observer (DocumentUploadStep, DefinitionError) refetches it and `ready`
  // re-pends — an unlatched gate then unmounts that consumer, whose unmount
  // settles the read, which remounts it: a livelock, not a hold.
  const { copy, ready } = useCopy();
  const { t } = useTranslation(['forms', 'applications']);
  const readyOnce = useRef(false);
  if (ready) readyOnce.current = true;
  const chrome = useMemo<WizardChrome>(() => ({
    phiGateEyebrow: copy('apply.wizard.phi_gate.eyebrow'),
    phiGateTitle: copy('apply.wizard.phi_gate.title'),
    publicityEyebrow: copy('apply.wizard.publicity.eyebrow'),
    publicityTitle: copy('apply.wizard.publicity.title'),
    regionLoadingEyebrow: copy('apply.wizard.region_step.loading_eyebrow'),
    regionLoadingBody: copy('apply.wizard.region_step.loading_body'),
    regionErrorEyebrow: copy('apply.wizard.region_step.error_eyebrow'),
    regionErrorTitle: copy('apply.wizard.region_step.error_title'),
    regionErrorBody: copy('apply.wizard.region_step.error_body'),
    regionErrorRetry: copy('apply.wizard.region_step.error_retry'),
    regionErrorToastBody: copy('apply.wizard.region_step.error_toast_body'),
    stepLabels: stepLabelsFrom(t),
  }), [copy, t]);
  // #2197: which slots are mandatory — the same list the server enforces.
  const requiredDocKinds = useRequiredDocumentKinds();
  const docSlots = useMemo(
    () => markRequiredSlots(buildDocSlots(copy, t), requiredDocKinds),
    [copy, t, requiredDocKinds],
  );
  return (
    // #2448: no top padding of its own — the step starts level with the
    // header toolbar, under <main>'s own top gutter.
    <div className="mx-auto w-full max-w-5xl pb-10">
      <DefinitionGate state={readyOnce.current ? state : { status: 'loading', retry: state.retry }}>
        {(version, form) => (
          <>
            {/* R2-B1 / R2-B18: the one form-level language line, above the walk. */}
            {/* #2431: the machine line stands down while the shell banner shows. */}
            <FormLanguageNotice deferToShellBanner localization={form.localization} className="mb-4 space-y-1 px-4" />
            <ApplicationWizardInner
              {...props}
              version={version}
              localization={form.localization}
              chrome={chrome}
              docSlots={docSlots}
            />
          </>
        )}
      </DefinitionGate>
    </div>
  );
}

function ApplicationWizardInner({
  flow = 'initial',
  dataSource = applicationsDataSource,
  version: publishedVersion,
  chrome,
  docSlots,
  localization,
}: ApplicationWizardProps & {
  version: FormDefinitionVersion;
  chrome: WizardChrome;
  docSlots: DocSlot[];
  /** What `useFormDefinition` reported about the language (#2109 R2). Absent
   *  on a surface that renders the wizard without the gate: English, no marks. */
  localization?: FormLocalization;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast: rawToast } = useToast();
  /**
   * 2026-10-11 (J1 c06): every toast this wizard raises is kept by handle and
   * closed when the wizard goes away. After a successful submit, /apply/status
   * still showed the documents step's red "please upload Family photos" toast
   * and two "also added elsewhere" toasts. A toast here is about a step of
   * this wizard; once the family has left it, it is about nothing on screen.
   */
  const raisedToastsRef = useRef(new Set<{ dismiss: () => void }>());
  const toast = useCallback((props: Parameters<typeof rawToast>[0]) => {
    const handle = rawToast(props);
    if (handle) raisedToastsRef.current.add(handle);
    return handle;
  }, [rawToast]);
  const dismissRaisedToasts = useCallback(() => {
    for (const handle of raisedToastsRef.current) handle.dismiss();
    raisedToastsRef.current.clear();
  }, []);
  useEffect(() => dismissRaisedToasts, [dismissRaisedToasts]);
  // #2109 R2 lane L3: validation is platform copy, so it follows the reader's
  // UI language through the four-tier lookup (validationMessages.ts), even on
  // a form that is not offered in it and renders in English.
  const { locale: uiLocale } = useLocale();
  const { t: tv, i18n } = useTranslation('validation');
  const { t } = useTranslation(['forms', 'common', 'grants']);
  const resolveMessage = useCallback(
    (req: ValidationRequest) => resolveValidationMessage(i18n, uiLocale, req),
    [i18n, uiLocale],
  );
  /** #2752 option (a): the renewal lines for published slots, in the form's language. */
  const formLocale = localization?.locale ?? 'en';
  // Read on every render (two lookups), memoised on the lines themselves: a
  // catalog that finishes loading after the form painted still swaps in.
  const renewalLines = flow === 'renewal' ? JSON.stringify(renewalLinesIn(i18n, formLocale)) : '[]';
  const renewalWording = useMemo(() => new Map<string, string>(JSON.parse(renewalLines)), [renewalLines]);
  const version = useMemo(() => withRenewalWording(publishedVersion, renewalWording), [publishedVersion, renewalWording]);
  /** R2-B18: the stage keys shown in English because their translation is
   *  stale — the only per-string marker. A slot the renewal line replaced is
   *  no longer the stale English string, so it carries no mark. */
  const staleKeys = useMemo(
    () => new Set((localization?.staleFallbacks ?? []).filter((key) => !renewalWording.has(key))),
    [localization, renewalWording],
  );
  /** Retired questions whose label stays English on this render (review step). */
  const retiredInEnglish = useMemo(() => new Set(localization?.retiredInEnglish ?? []), [localization]);
  const staleMark = useCallback(
    (key: string) => (staleKeys.has(key) ? <ShownInEnglish /> : null),
    [staleKeys],
  );
  const { user } = useAuth();

  const [app, setApp] = useState<FamilyApplication | null>(null);
  // Victor 2026-10-11 (option a): money bands given on the other country's list are cleared on screen and the family
  // chooses again; the earlier answer is named under the question until they do. Keyed `section.key`.
  const [repicks, setRepicks] = useState<ReadonlyMap<string, CountryRepick>>(new Map());
  /** #2431: below md the renewal callout shows its title only until opened. */
  const [renewalDetailsOpen, setRenewalDetailsOpen] = useState(false);
  const renewalDetailsId = useId();
  /** #2440: the step id the server last recorded for this draft. */
  const savedResumeStepRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  // #2850: the server refused to start a renewal because this account has no
  // TRF grant to renew. The page says so and points at /apply instead of a
  // refresh that can never succeed.
  const [renewalNotEligible, setRenewalNotEligible] = useState(false);
  // #2687: the server refused because renewals stop at Year 3. A Year 4 is a
  // manual TRF act, so the page says so instead of a refresh-and-retry toast.
  const [renewalYearCap, setRenewalYearCap] = useState(false);
  // #2792 follow-up: the create refused on an eligibility miss. Holds the
  // server's own sentence for an English reader ('' for everyone else, whose
  // page shows the contact line alone).
  const [prescreenMiss, setPrescreenMiss] = useState<{ message: string } | null>(null);
  // Wave launch-w1014 L3-a: the create refused for a reason the family can
  // act on here (most often: the code or link from sign-up never reached the
  // create — a closed tab loses the sessionStorage stash). Rendered as a stop
  // with a code box, never as a toast over an empty page.
  const [startRefused, setStartRefused] = useState<{ reason: StartRefusalReason; message?: string; opensOn?: string } | null>(null);
  // Bumped by the stop's code box: re-runs the mount load, whose getMine 404
  // creates the draft again with the newly stashed code.
  const [startAttempt, setStartAttempt] = useState(0);
  // #2199: TRF has closed the platform to Canadian families — read off the
  // public gate, or learned from the server's own refusal of a save or submit
  // (the switch flipped after this page loaded).
  const canadaSwitch = useCanadaIntakeClosed();
  const [canadaRefusedByServer, setCanadaRefusedByServer] = useState(false);
  const canadaIntakeClosed = canadaSwitch || canadaRefusedByServer;
  const [stepIndex, setStepIndex] = useState(0);
  // #2448: held from the first load — a section save answers with the stored
  // application, which carries no prefill.
  const [prefillBirthYear, setPrefillBirthYear] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // #2754 review: saves on the wire, silent ones included. The status line's
  // "Saving…" reads this, never the pause timer: a pending timer is not a save.
  const [savesInFlight, setSavesInFlight] = useState(0);

  // The definition drives the walk. `STEPS` keeps its name so every reference
  // below reads exactly as it did when it was a module constant — the only
  // thing that changed is where the screens come from.
  const STEPS = useMemo(() => buildSteps(toApplicationScreens(version), chrome, chrome.stepLabels), [version, chrome]);
  const WIZARD_STEPS = useMemo(() => toWizardSteps(STEPS), [STEPS]);
  /** #2199: the step that asks the Region question (`intake.country`); -1 when none does. */
  const regionStepIndex = useMemo(
    () => STEPS.findIndex((s) => s.type === 'screen'
      && s.screen.groups.some((g) => g.fields.some((ref) => ref.section === 'intake' && ref.key === 'country'))),
    [STEPS],
  );
  /** `findFieldSpec`'s replacement — the published version's live fields. */
  const fieldsByPath = useMemo(() => fieldIndex(version), [version]);
  const findFieldSpec = useCallback(
    (section: SectionKey | string, key: string): FieldSpec | undefined =>
      fieldsByPath.get(`${section}.${key}`),
    [fieldsByPath],
  );
  /**
   * The other half of the index — questions this version has RETIRED (#1114).
   *
   * Nothing asks them: `toApplicationScreens` drops them from the walk and they
   * appear on no step. They exist here for the review step alone, which has to
   * read back an answer this family gave before the question was retired. Staff
   * have always kept those answers visible (D-SE-13); the family that typed them
   * should not be the one party who cannot see them.
   */
  const retiredFieldsBySection = useMemo(
    () => sectionFieldsFrom(version, { retired: true }),
    [version],
  );
  /**
   * D-FB-11 / #1639: which questions is this family actually being asked?
   *
   * `visible_when` reached every consumer except the one families walk — the
   * builder preview honoured it, the pre-screen store gate and the submit gate
   * honoured it, and the wizard asked the question of everyone because
   * `toFieldSpec` dropped the property. The rule now lives in
   * `lib/formDefinitions.ts` and is CALLED here rather than restated: the
   * preview and this component have to agree about the same published form, and
   * a second implementation is how they stopped agreeing in the first place.
   *
   * The driver index is built from the version (ids, not paths — a condition
   * names a question, and its driver may sit in another section); the answers
   * are live `sectionData`, so hiding and revealing follow typing.
   */
  const drivers = useMemo<DriverIndex>(() => driverIndex(version?.fields), [version]);
  /**
   * Date rules to enforce at step-advance: the three seeded #1111 ones, plus
   * whatever the published version declares (#1838).
   *
   * The seeded three come FIRST and win on a path collision, so their exact
   * wording survives a future publish that also marks those two fields. They
   * are listed here rather than left implicit because they are
   * version-independent: they hold on a pre-seed dev box where `version` is
   * null, and the submit route enforces them the same way.
   */
  const dateRules = useMemo(
    () => localizeDateRules(
      [...SEEDED_DATE_RULES, ...rulesFromFields(version?.fields ?? [])],
      version?.fields ?? [],
      resolveMessage,
    ),
    [version, resolveMessage],
  );

  const { savedAt, markSaved } = useTimedSave();

  // ── #1055: what THIS tab changed ──────────────────────────────────────────
  // A section save used to PATCH the whole section built from local state, so
  // a second tab's blur-save silently reverted every sibling field the first
  // tab had just changed (verified: A sets city, stale B edits zip, B's save
  // reverts A's city — no warning, silent loss on an application). The backend
  // has always written per-key dotted `$set` (FamilyApplication.saveSection);
  // the client was the one flattening a section into a last-writer blob.
  //
  // So: every write into `sectionData` also records its dotted PATH here, and
  // a save carries those paths and nothing else. A ref, not state — nothing
  // renders from it, and a save must read the paths as they are at send time
  // rather than through a render-old closure.
  //
  // Each path carries the sequence number of the touch that dirtied it. A save
  // clears a path only if the number it sent is still the current one, so an
  // edit made WHILE that save was in flight keeps its dirty flag instead of
  // being cleared by a response that predates it.
  const dirtyRef = useRef<Record<SectionKey, Map<string, number>>>({
    intake: new Map(), patient: new Map(), household: new Map(), finances: new Map(), travel: new Map(),
  });
  const touchSeqRef = useRef(0);

  // ── #2727: unsaved edits and the typing autosave ──────────────────────────
  /** What the family changed that has not reached the server yet. The status
   *  line reads it: "Saved ✓" is only true when this is empty. */
  const editedRef = useRef(new Set<SaveTarget>());
  const [unsaved, setUnsaved] = useState(false);
  const autosaveRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; targets: Set<SaveTarget> }>({ timer: null, targets: new Set() });
  const isClean = useCallback((target: SaveTarget) => dirtyRef.current[target].size === 0, []);
  /** Re-read after every successful save: a target is settled once nothing
   *  dirty is left in it (an edit made while that save was in flight keeps it
   *  unsaved, by the same sequence rule as #1055). */
  const refreshUnsaved = useCallback(() => {
    for (const target of [...editedRef.current]) {
      if (isClean(target)) editedRef.current.delete(target);
    }
    setUnsaved(editedRef.current.size > 0);
  }, [isClean]);

  const markDirty = useCallback((sectionKey: SectionKey, ...paths: string[]) => {
    const marks = dirtyRef.current[sectionKey];
    for (const path of paths) marks.set(path, (touchSeqRef.current += 1));
  }, []);

  /** The payload for a section save: one entry per dirty path, read out of the
   *  section snapshot being saved. A path the wizard CLEARED (address
   *  verification) reads back `undefined`, which a dotted `$set` cannot
   *  express — it is sent as `null`, and every reader already treats null as
   *  absent (ApplicationDetailPage renders no chip for it).
   *
   *  #3111: an answer the server would refuse (a half-typed email, phone or
   *  postal code, a number past its bound) is HELD BACK and stays dirty. The
   *  server now refuses the whole save over one malformed value, and the
   *  typing autosave fires on a 1.2 s pause, mid-word; sending it would turn
   *  every pause into a "couldn't save" toast and hold back the answers beside
   *  it. Step-advance still names the problem (`fieldValueProblem`). Dates are
   *  not held: the server does not refuse a draft date. */
  const takeDirty = useCallback((sectionKey: SectionKey, data: Record<string, unknown>) => {
    const sent = new Map(dirtyRef.current[sectionKey]);
    const payload: Record<string, unknown> = {};
    for (const path of [...sent.keys()]) {
      const value = get(data, path);
      const spec = findFieldSpec(sectionKey, path);
      if (spec && spec.type !== 'date' && fieldValueProblem(spec, value) !== null) {
        sent.delete(path);
        continue;
      }
      payload[path] = value === undefined ? null : value;
    }
    return { payload, sent };
  }, [findFieldSpec]);

  const clearSent = useCallback((sectionKey: SectionKey, sent: Map<string, number>) => {
    const marks = dirtyRef.current[sectionKey];
    for (const [path, seq] of sent) {
      if (marks.get(path) === seq) marks.delete(path);
    }
  }, []);

  // Per-section local form state
  const [sectionData, setSectionData] = useState<Record<SectionKey, Record<string, unknown>>>({
    intake: {},
    patient: {},
    household: {},
    finances: {},
    travel: {},
  });
  /** Is this question asked at all, given the answers so far? (#1639) */
  const revealed = useCallback(
    (spec: FieldSpec | undefined): boolean =>
      isRevealed(spec?.visibleWhen, drivers, sectionData as SectionAnswers),
    [drivers, sectionData],
  );
  /**
   * A screen's groups, resolved to the questions this family is actually being
   * asked. ONE derivation, used by the renderer, the step gate and the walk —
   * the defect this replaces was the renderer and the gate disagreeing about a
   * hidden question, so they must not be able to read different lists again.
   * A group left with nothing keeps its heading off the screen with it.
   */
  const askedGroups = useCallback(
    (screen: ApplicationScreen) =>
      screen.groups
        // `index` is the group's place in the DEFINITION — its translation
        // stage key (#2109 R2-B18) — which the filter below would lose.
        .map((group, index) => ({
          ...group,
          index,
          fields: group.fields.filter((ref) => revealed(findFieldSpec(ref.section, ref.key))),
        }))
        .filter((group) => group.fields.length > 0),
    [revealed, findFieldSpec],
  );
  const [uploadedDocs, setUploadedDocs] = useState<ApplicationDocumentMeta[]>([]);
  // #2377: the required kinds the last documents-step refusal named. Set only
  // when the gate blocks, so the step moves focus once per refusal.
  const [missingDocKinds, setMissingDocKinds] = useState<string[]>([]);
  // Field gate (2027 match + QA fix wave): `${section}.${key}` → the message
  // that blocked the last advance attempt ("This question is required." or a
  // range/format problem from fieldValueProblem). A field clears its own flag
  // only when its value actually resolves the problem (typing whitespace into
  // a required field no longer un-flags it); the map resets on a successful
  // advance. Server floor (applications.js EC-3 trio) is unchanged.
  const [missingFields, setMissingFields] = useState<Map<string, string>>(new Map());
  // #2586: field key → the sentence a date control would show for the parts
  // the family has typed so far (null = empty or a real date). A half-typed
  // date STORES null, so the gate below cannot see it in `sectionData`; the
  // control reports it here instead. A ref, not state: it is read on Save,
  // never rendered.
  const dateDraftsRef = useRef(new Map<string, string>());
  const dateDraftRegistry = useMemo<DateDraftRegistry>(() => ({
    report: (fieldKey, problem) => {
      if (problem) dateDraftsRef.current.set(fieldKey, problem);
      else dateDraftsRef.current.delete(fieldKey);
    },
    // The wizard's inline marker already says it; the control stays quiet.
    flagged: (fieldKey) => [...missingFields.keys()].some((k) => k.split('.').slice(1).join('.') === fieldKey),
  }), [missingFields]);
  // True when the final data-sharing consent was already recorded on a prior
  // visit (e.g. a submit that failed after consent). We then show an explicit
  // Submit button instead of auto-resubmitting on re-mount.
  const [alreadyConsented, setAlreadyConsented] = useState(false);
  // #329/D-7 + #1636: does a PUBLISHED pipeda_authorization doc exist?
  //
  // THREE states, not two, and the third is the whole of #1636. This was a
  // boolean, and `.catch(() => setPipedaPublished(false))` made "the request
  // failed" identical to "Canada has no consent document" — which falls through
  // to the US arm. One dropped `GET /api/legal` therefore showed a Canadian
  // family the US HIPAA release and recorded their acceptance under
  // `phi_authorization` in an APPEND-ONLY log (`backend/src/models/ConsentEvent.js`:
  // no update, no delete, by design), with no retry, no log and nothing on
  // screen. Nothing downstream would have caught it either: the server accepts
  // either document id for a family (B-580) and never re-checks which one a CA
  // family should have been shown, so the client's guess is the only one made.
  //
  // 'unknown' therefore HOLDS the step instead of resolving it — but it must
  // not fail closed into a dead end (see the render), because a Canadian family
  // that cannot apply is a family TRF never hears from.
  const [pipedaLookup, setPipedaLookup] = useState<PipedaLookup>('idle');
  // Bumped by the family's own "Try again" — the effect's other trigger.
  const [pipedaAttempt, setPipedaAttempt] = useState(0);

  // For the renewal flow, default the intake `application_type` to the renewal
  // option (intakeFields.ts: 'renewal_year_2') when the hydrated draft leaves it
  // empty, so the field reflects the flow. Server pre-fill still wins if present.
  const seedRenewalIntake = useCallback(
    (intake: Record<string, unknown>): Record<string, unknown> => {
      if (flow !== 'renewal') return intake;
      if (intake.application_type) return intake;
      return { ...intake, application_type: 'renewal_year_2' };
    },
    [flow],
  );

  // ── Mount: load or create draft ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Flow-aware mount guard (mirrors the file's existing `user.role` shape):
      //  - initial: EC-1 (B-094) admitted parents already have role 'parent' →
      //    they don't belong in the new-applicant flow → /dashboard.
      //  - renewal: only admitted parents may renew → non-parents → /dashboard.
      const isParent = user?.role === 'parent';
      if (flow === 'initial' && isParent) { navigate('/dashboard', { replace: true }); return; }
      if (flow === 'renewal' && !isParent) { navigate('/dashboard', { replace: true }); return; }
      // L3-a: whether a failure below came from the create, not the read.
      let creating = false;
      try {
        let result = await dataSource.getMine();
        // 404 / no data → create draft. #2659: so does a decided renewal whose
        // grant year is over — the server flags it, and its POST starts the
        // next year's renewal instead of handing the old one back.
        if (!result.data || (flow === 'renewal' && result.data.next_renewal_open === true)) {
          creating = true;
          result = await dataSource.createDraft();
          creating = false;
          // #2332: the family now HAS a draft — /bills says "Continue", not "Start".
          void syncMineCache(queryClient, dataSource, result);
        }
        if (cancelled) return;
        const loaded = result.data!;
        setApp(loaded);
        const storedIntake = seedSection(loaded, 'intake');
        const seededIntake = seedRenewalIntake(storedIntake);
        // #2448: what the pre-screen already said (hospital, referrer, the
        // hospital's city), offered to EMPTY patient fields only, and marked
        // dirty so Save and continue keeps it.
        const storedPatient = seedSection(loaded, 'patient');
        const prefilled = flow === 'initial' ? prefillEmpty(storedPatient, loaded.prefill?.patient, (k) => !!findFieldSpec('patient', k)) : [];
        const shownPatient = prefilled.length ? { ...storedPatient, ...Object.fromEntries(prefilled) } : storedPatient;
        // #2523 follow-up: a renewal pre-filled from a Family answered on the
        // US lists (admitted before #2523) would show a Canadian family "miles"
        // and "$US" under Canadian questions. Each such answer moves to its
        // same-band Canadian value and is marked dirty, so the save keeps it.
        const aligned = alignCountryAnswers({
          intake: seededIntake,
          patient: shownPatient,
          household: seedSection(loaded, 'household'),
          finances: seedSection(loaded, 'finances'),
          travel: seedSection(loaded, 'travel'),
        }, findFieldSpec);
        setSectionData(aligned.sections);
        for (const [section, key] of aligned.moved) markDirty(section, key);
        setRepicks(new Map(aligned.repick.map((r) => [`${r.section}.${r.key}`, r])));
        // #2440: the B-403 clamp judges the intake the wizard SHOWS, which is
        // what the advance gate judges too. On the stored draft alone a renewal
        // without a stored application_type read as an unfinished first screen
        // and capped the recorded step at step 1, although that screen arrives
        // already answered. The pre-screen patient offer stays out: it is an
        // offer the family has not reviewed, not a step they reached.
        const shown: FamilyApplication = { ...loaded, intake: seededIntake };
        // #1055: a save now carries only what this tab touched, so a value the
        // CLIENT seeds has to declare itself dirty or it would never reach the
        // server. Under whole-section saves it rode along for free.
        if (seededIntake !== storedIntake) markDirty('intake', 'application_type');
        if (prefilled.length) markDirty('patient', ...prefilled.map(([k]) => k));
        if (flow === 'initial') setPrefillBirthYear(loaded.prefill?.birth_year ?? null);

        // Submitted/terminal applications are read-only → show status instead.
        // needs_more_info is editable: the applicant must be able to revise and
        // resubmit (backend allows section saves + needs_more_info→submitted), so
        // it must NOT bounce to /apply/status or the "Continue your application"
        // button on the status page becomes an infinite redirect loop.
        // Checked BEFORE the document fetch below so a read-only application
        // still costs exactly one round trip.
        // #2585: /apply/status reads the family's INITIAL application, so a
        // renewal family reopening /renew after submitting saw their first
        // application from a year ago (or a 404 bounce). The renewal flow
        // exits to the surface that shows the renewal's own state, the same
        // one its submit lands on.
        if (loaded.status !== 'draft' && loaded.status !== 'needs_more_info') {
          navigate(flow === 'renewal' ? '/bills?tab=grants' : '/apply/status', { replace: true });
          return;
        }

        savedResumeStepRef.current = loaded.resume_step ?? null;
        // B-DRAFT-1: resume where they left off. B-403: on the renewal flow the
        // draft is pre-filled out of wizard order, so guard against landing
        // past a screen whose required fields were never answered.
        //
        // Computed TWICE, on purpose. This first pass knows nothing about
        // uploads and is the floor: if the document fetch below fails, the
        // family lands exactly where they landed before #1057 existed, and the
        // failure surfaces through the same catch it always did. Nothing is
        // painted in between — `loading` stays true until the `finally`.
        // #2527: a request TRF sent back opens on the first step holding what
        // TRF asked about — not where the family last stood, which was the
        // end of the walk when they submitted. Ahead of both passes below.
        const flaggedAt = loaded.status === 'needs_more_info' ? firstFlaggedStep(flagKeysByStep(STEPS), loaded) : -1;
        setStepIndex(flaggedAt >= 0 ? flaggedAt : computeResumeIndex(shown, STEPS, findFieldSpec, flow === 'renewal', false, drivers));

        // #1057: the document list is loaded BEFORE the pointer is finalized.
        // An upload is data the family put in, and a pointer computed against
        // a list that hasn't arrived yet can only ever see zero — which is why
        // a family who uploaded and closed came back two steps short.
        const docsRes = await dataSource.listDocuments(loaded._id);
        if (cancelled) return;
        const docs = oldestFirst(docsRes.data ?? []);
        setUploadedDocs(docs);
        if (docs.length > 0 && flaggedAt < 0) {
          setStepIndex(computeResumeIndex(shown, STEPS, findFieldSpec, flow === 'renewal', true, drivers));
        }
      } catch (err: unknown) {
        if (cancelled) return;
        // #2819: the pre-screen answers live in the tab that answered them
        // (sessionStorage). A family who pre-screened in another tab or on
        // another device, or a seeded applicant who never did, reaches here
        // with none — and "refresh" never fixed that. Send them to answer it;
        // /apply shows the pre-screen to a signed-in applicant on this state
        // instead of bouncing back, which is what breaks the loop.
        const toPrescreen = () => navigate('/apply', { replace: true, state: { prescreenRequired: true } });
        if (flow === 'initial' && isPrescreenRequired(err)) {
          toPrescreen();
          return;
        }
        const showStartRefused = (refusal: unknown) => setStartRefused({
          reason: startRefusalReason(refusal),
          message: englishServerMessage(refusal, i18n.language ?? 'en'),
          opensOn: newFamiliesOpenOnOf(refusal),
        });
        const toMissStop = (refusal: unknown) => setPrescreenMiss({ message: englishServerMessage(refusal, i18n.language ?? 'en') ?? '' });
        if (flow === 'initial' && isPrescreenMiss(err)) { toMissStop(err); return; }
        // #2850: the create inside the try (no draft, or a spent year) refused.
        if (flow === 'renewal' && isRenewalNotEligible(err)) { setRenewalNotEligible(true); return; }
        if (flow === 'renewal' && isRenewalYearCap(err)) { setRenewalYearCap(true); return; }
        // Wave launch-w1014 L3-a: the create inside the try refused — the same
        // on-page stop as the 404 path's create below.
        if (flow === 'initial' && creating) { showStartRefused(err); return; }
        // If 404 (no draft yet), try to create
        const axiosErr = err as { response?: { status?: number } };
        if (axiosErr?.response?.status === 404) {
          try {
            const created = await dataSource.createDraft();
            void syncMineCache(queryClient, dataSource, created); // #2332, as above
            if (cancelled) return;
            const newApp = created.data!;
            setApp(newApp);
            // #2523 follow-up (B-676): a renewal's FIRST open lands here (getMine 404 → createDraft), so the
            // pre-filled US-list answers move to the family's own country list here too, as on the resume path.
            const alignedNew = alignCountryAnswers({
              intake: seedRenewalIntake(seedSection(newApp, 'intake')),
              patient: seedSection(newApp, 'patient'),
              household: seedSection(newApp, 'household'),
              finances: seedSection(newApp, 'finances'),
              travel: seedSection(newApp, 'travel'),
            }, findFieldSpec);
            setSectionData(alignedNew.sections);
            for (const [section, key] of alignedNew.moved) markDirty(section, key);
            setRepicks(new Map(alignedNew.repick.map((r) => [`${r.section}.${r.key}`, r])));
          } catch (createErr: unknown) {
            if (cancelled) return;
            if (flow === 'initial' && isPrescreenRequired(createErr)) { toPrescreen(); return; }
            if (flow === 'initial' && isPrescreenMiss(createErr)) { toMissStop(createErr); return; }
            // #2850: getMine 404 → createDraft refused: this account has no grant to renew.
            if (flow === 'renewal' && isRenewalNotEligible(createErr)) { setRenewalNotEligible(true); return; }
            // #2687: getMine 404 → createDraft refused: the grant is in its final year.
            if (flow === 'renewal' && isRenewalYearCap(createErr)) { setRenewalYearCap(true); return; }
            // Wave launch-w1014 L3-a: every other refusal of a FIRST application's
            // create is a stop on the page. `app` stays null, and a null app used
            // to render nothing at all.
            if (flow === 'initial') { showStartRefused(createErr); return; }
            toast({ title: t(wordingFor(i18n, flow, 'forms:wizard.startFailedTitle', 'forms:wizard.startFailedTitleRenewal')), description: t('forms:wizard.refreshAndRetry'), variant: 'destructive' });
          }
        } else {
          toast({ title: t(wordingFor(i18n, flow, 'forms:wizard.loadFailedTitle', 'forms:wizard.loadFailedTitleRenewal')), description: t('forms:wizard.refreshAndRetry'), variant: 'destructive' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAttempt]);

  // ── #2199: the Canada stop ──
  // With the switch on, a DRAFT whose Region is Canada shows the notice in
  // place of every step after the Region question — the family picked Canada
  // and pressed Save and continue, or resumed a draft saved as Canadian. A
  // renewal whose family record is Canadian (the draft is pre-filled from it)
  // shows it from the intake step on. A family sent back with Incomplete is
  // already inside and never sees it; the server keeps the same rule.
  const answeredCanada = sectionData.intake?.country === 'CA';
  const renewalOnRecordCanada = flow === 'renewal'
    && String(app?.intake?.country ?? '').trim().toUpperCase() === 'CA';
  const canadaStop = canadaIntakeClosed && app?.status === 'draft'
    && (renewalOnRecordCanada || (answeredCanada && stepIndex > regionStepIndex));
  /** Read by the saves: a Canadian Region answer is not sent while the switch is on. */
  const canadaHoldRef = useRef(false);
  canadaHoldRef.current = canadaIntakeClosed && app?.status === 'draft';

  // ── Save a section ──
  // `silent` saves (the fire-and-forget autosave on field blur) must NOT flip the
  // shared `saving` flag — that flag disables Back / Save & continue, so a blur
  // autosave racing a click would eat the click (the "needs 2-3 clicks to advance"
  // bug). Errors still surface (B-014/048/051); only the button-disabling state is
  // decoupled. The explicit Save & continue passes silent=false (default).
  //
  // #2756 item 2: saves of ONE section run one after another. A pause autosave
  // still in flight and a later blur save used to race, and whichever landed
  // last won, so an older value could overwrite a newer one. Each save is sent
  // only once the section's previous save has settled, success or failure, and
  // it reads its payload when it is SENT, from the latest section state
  // (`sectionDataRef`), so a save queued behind a slow one never carries an
  // older value than the screen holds (#2818 review). Different sections still
  // save in parallel. The unmount flush queues on the same chain.
  //
  // #2756 item 1: `unload` is the pagehide flush. It goes through the source's
  // keepalive save, which the browser lets finish after a desktop tab closes,
  // and it does not queue: a queued request would never leave a closing page.
  const saveChainRef = useRef<Partial<Record<SectionKey, Promise<void>>>>({});
  const sectionDataRef = useRef(sectionData);
  sectionDataRef.current = sectionData;
  const saveSection = useCallback(async (sectionKey: SectionKey, { silent = false, data, unload = false }: { silent?: boolean; data?: Record<string, unknown>; unload?: boolean } = {}) => {
    if (!app) return;
    // #2199: the server would refuse a Canadian Region answer while the switch
    // is on. It is held here (still dirty) and the notice says why, instead of
    // a "couldn't save" toast on the click that picked Canada.
    if (sectionKey === 'intake' && canadaHoldRef.current && sectionDataRef.current.intake?.country === 'CA') return;
    if (!silent) setSaving(true);
    setSavesInFlight((n) => n + 1);
    try {
      // `data` overrides the state read for saves that must carry values
      // committed in the SAME tick (the #1059 address decision): state written
      // via setSectionData right before this call isn't visible to this
      // closure yet, and saving the stale snapshot would drop the decision.
      //
      // #1055: only the paths this tab dirtied travel. `sent` is cleared on
      // SUCCESS only — a failed save keeps its paths, so the next save (blur,
      // advance, or unmount flush) retries them instead of losing the edit.
      const appId = app._id;
      // `data` (a same-tick override) is used only by a save that is not
      // queued behind another; a queued save reads the latest state instead,
      // which by then includes the override's values.
      const queued = !unload && saveChainRef.current[sectionKey] !== undefined;
      let sent = new Map<string, number>();
      const send = () => {
        const snapshot = data && !queued ? data : sectionDataRef.current[sectionKey];
        const taken = takeDirty(sectionKey, snapshot);
        sent = taken.sent;
        return unload && dataSource.saveSectionOnUnload
          ? dataSource.saveSectionOnUnload(appId, sectionKey, taken.payload)
          : dataSource.saveSection(appId, sectionKey, taken.payload);
      };
      let request: ReturnType<typeof send>;
      if (unload) {
        request = send();
      } else {
        request = (saveChainRef.current[sectionKey] ?? Promise.resolve()).then(send);
        saveChainRef.current[sectionKey] = request.then(() => undefined, () => undefined);
      }
      const res = await request;
      if (res.data) setApp(res.data);
      clearSent(sectionKey, sent);
      // A save that carried nothing while a malformed answer is held back
      // (#3111) is not "Saved": the family's answer never left the page.
      const heldBack = sent.size === 0 && dirtyRef.current[sectionKey].size > 0;
      if (!heldBack) markSaved();
      refreshUnsaved();
    } catch (err) {
      // #2199: the switch was flipped after this page read it. The edit stays
      // dirty; the notice takes the next step's place.
      if (isCanadaIntakeClosedError(err)) {
        setCanadaRefusedByServer(true);
        return;
      }
      toast({ title: t('forms:wizard.saveFailedTitle'), description: t('forms:wizard.saveFailedBody'), variant: 'destructive' });
      throw new Error('save_failed', { cause: err });
    } finally {
      if (!silent) setSaving(false);
      setSavesInFlight((n) => n - 1);
    }
  }, [app, sectionData, markSaved, toast, dataSource, takeDirty, clearSent, refreshUnsaved, t]);

  // B-162: pills (multi/select) and checkboxes commit their value on CLICK, not
  // blur, so a selection made right before a reload was never saved — the field
  // only blurs on a LATER interaction. Each such click bumps `clickSave` (target
  // + a monotonic nonce, so repeat clicks on the same step still change identity)
  // and the effect fires the silent save AFTER React commits the new state, so
  // the payload includes the click. The nonce is the SOLE trigger: we read the
  // savers from a ref rather than list them in the deps because saveSection's
  // identity also changes on every setApp from a completed save — depending on it
  // would re-fire (and, with a shared pending flag, drop) saves. One click → one
  // deterministic save with post-commit state.
  const [clickSave, setClickSave] = useState<{ target: SectionKey | null; nonce: number }>({ target: null, nonce: 0 });
  const saversRef = useRef({ saveSection });
  saversRef.current = { saveSection };
  useEffect(() => {
    if (!clickSave.target) return;
    saversRef.current.saveSection(clickSave.target, { silent: true }).catch(() => {});
  }, [clickSave]);

  // #2727: a typed answer saves itself once typing pauses, and everything
  // unsaved is sent the moment the page is hidden or closed. Both mirror the
  // blur save (silent, dirty paths only) and read the savers from the same ref
  // as the click save, so they always send the latest state.
  const cancelAutosave = useCallback((target?: SaveTarget) => {
    const a = autosaveRef.current;
    if (target) a.targets.delete(target);
    else a.targets.clear();
    if (a.targets.size === 0 && a.timer) {
      clearTimeout(a.timer);
      a.timer = null;
    }
  }, []);

  const runAutosave = useCallback((targets: Iterable<SaveTarget>, { unload = false }: { unload?: boolean } = {}) => {
    const { saveSection: ss } = saversRef.current;
    for (const target of targets) {
      if (isClean(target)) continue;
      ss(target, { silent: true, unload }).catch(() => {});
    }
  }, [isClean]);

  /** A family edit: mark it unsaved and (re)start the pause timer. */
  const noteEdit = useCallback((target: SaveTarget) => {
    editedRef.current.add(target);
    setUnsaved(true);
    const a = autosaveRef.current;
    a.targets.add(target);
    if (a.timer) clearTimeout(a.timer);
    a.timer = setTimeout(() => {
      const due = [...a.targets];
      a.timer = null;
      a.targets.clear();
      runAutosave(due);
    }, AUTOSAVE_DELAY_MS);
  }, [runAutosave]);

  useEffect(() => {
    const flushAll = () => {
      cancelAutosave();
      runAutosave(ALL_SECTIONS);
    };
    // #2756 item 1: pagehide is the page going away (a desktop tab close), so
    // its flush is the keepalive save. A hidden tab (a phone backgrounding the
    // app) keeps the ordinary save, which can still refresh an expired token.
    const flushOnPageHide = () => {
      cancelAutosave();
      runAutosave(ALL_SECTIONS, { unload: true });
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushAll(); };
    window.addEventListener('pagehide', flushOnPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushOnPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      // The unmount flush below sends what is dirty; the timer must not fire
      // into an unmounted wizard after it.
      const a = autosaveRef.current;
      if (a.timer) clearTimeout(a.timer);
      a.timer = null;
      a.targets.clear();
    };
  }, [cancelAutosave, runAutosave]);

  // Flush the current step's latest local state on unmount (e.g. the user
  // navigates away mid-step) so an in-flight blur autosave that hadn't landed
  // isn't lost. Reads from a ref to avoid a stale closure. Screen steps span
  // sections — flush every section the screen touches.
  const flushRef = useRef<{ appId: string | null; stepIndex: number; sectionData: typeof sectionData; steps: StepKind[] }>({ appId: null, stepIndex: 0, sectionData, steps: STEPS });
  flushRef.current = { appId: app?._id ?? null, stepIndex, sectionData, steps: STEPS };
  useEffect(() => () => {
    const f = flushRef.current;
    if (!f.appId) return;
    const st = f.steps[f.stepIndex];
    if (st?.type === 'screen') {
      screenSections(st.screen).forEach((sec) => {
        // #1055: the flush obeys the same rule as every other save — dirty
        // paths only. Nothing dirty means nothing to flush, so it stays quiet
        // instead of re-writing the whole section on every unmount.
        // #2818 review: queued behind this section's saves, and the payload is
        // read when it is sent, so an older queued save cannot land after it.
        const flush = async () => {
          const { payload } = takeDirty(sec, sectionDataRef.current[sec] ?? f.sectionData[sec]);
          if (Object.keys(payload).length === 0) return;
          await dataSource.saveSection(f.appId!, sec, payload);
        };
        const chained = (saveChainRef.current[sec] ?? Promise.resolve()).then(flush);
        saveChainRef.current[sec] = chained.then(() => undefined, () => undefined);
        void chained.catch(() => {});
      });
    }
  }, [dataSource, takeDirty]);

  // #329/D-7: only for CA applicants — check whether a PUBLISHED
  // pipeda_authorization doc exists via the public /api/legal index (which
  // excludes drafts). US / unset country never fires this effect at all, so
  // there is zero added behavior (network or otherwise) for the byte-identical
  // US path (D-7 criterion #1).
  //
  // #1636: a failure is now a THIRD answer rather than a silent 'no'.
  //  · it retries once on its own, because the reported failure is a single
  //    dropped request and a second ask costs the family nothing;
  //  · then it says so — console for us, a destructive toast for the family,
  //    the same register `DisclosureGate.handleAgree` uses for a failed consent
  //    POST — instead of the previous empty `.catch`;
  //  · and `pipedaAttempt` lets the family ask again from the step itself, so a
  //    network that comes back resolves the branch without a reload. The old
  //    dependency array meant one transient failure stuck for the whole mount.
  //
  // What "says so" reaches: the toast and the surface reach the FAMILY. The
  // staff half goes through `reportClientError` (#1654) — the one client→server
  // error channel — which logs the token to the family's console AND posts it
  // (token + bare route + timestamp, never the error itself) to
  // /api/client-errors, where staff read it on /admin/stats. The token is the
  // grep handle on both ends.
  useEffect(() => {
    const country = sectionData.intake?.country;
    if (country !== 'CA') { setPipedaLookup('idle'); return; }
    let cancelled = false;
    setPipedaLookup('loading');
    (async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await api.legal.index();
          if (cancelled) return;
          setPipedaLookup(
            res.data?.some((d) => d.document_id === 'pipeda_authorization') ? 'published' : 'unpublished',
          );
          return;
        } catch (error) {
          if (cancelled) return;
          if (attempt === 0) continue;
          // One token, not a sentence: a log line has no reader to edit it for,
          // so it stays out of the copy registry (copy-coverage's covered-prose
          // guard) and stays greppable in a structured log.
          reportClientError('apply.ca_consent.legal_index_unreadable', error);
          setPipedaLookup('error');
          toast({
            title: chrome.regionErrorTitle,
            description: chrome.regionErrorToastBody,
            variant: 'destructive',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sectionData.intake?.country, pipedaAttempt, toast,
      chrome.regionErrorTitle, chrome.regionErrorToastBody]);

  // ── #1059: address validation on the contact screen ──
  // `checkingAddress` covers the proxy round-trip (disables nav so a double
  // click can't fire two validations); `addressReview` carries the verdict a
  // family must decide on; the ref bridges the dialog's buttons back into
  // handleSaveAndContinue's await.
  const [checkingAddress, setCheckingAddress] = useState(false);
  const [addressReview, setAddressReview] = useState<{ entered: AddressCandidate; result: AddressValidationData } | null>(null);
  const addressChoiceRef = useRef<((choice: AddressChoice) => void) | null>(null);

  const collectEnteredAddress = useCallback((): AddressCandidate & { country: string } => ({
    street: String(get(sectionData.household, 'address.street') ?? ''),
    apt: String(get(sectionData.household, 'address.apt') ?? ''),
    city: String(get(sectionData.household, 'address.city') ?? ''),
    state: String(sectionData.intake?.residence_state ?? ''),
    zip: String(get(sectionData.household, 'address.zip') ?? ''),
    country: String(sectionData.intake?.country ?? 'US'),
  }), [sectionData.household, sectionData.intake]);

  const resolveAddressGroup = useCallback(async (): Promise<AddressOutcome> => {
    const entered = collectEnteredAddress();
    let data: AddressValidationData | undefined;
    setCheckingAddress(true);
    try {
      data = (await api.addressValidation.validate(entered)).data ?? undefined;
    } catch {
      // Transport or provider trouble is OURS, never the family's problem —
      // fail OPEN and record honestly that no check happened.
      return { action: 'proceed', status: 'unchecked' };
    } finally {
      setCheckingAddress(false);
    }
    // Dark mode stays byte-identical: no marker, no dialog, no trace.
    if (!data || data.mode === 'off') return { action: 'proceed', status: null };
    if (data.verdict === 'confirmed') return { action: 'proceed', status: 'confirmed' };
    if (data.verdict !== 'corrected' && data.verdict !== 'unknown') {
      // A shape this client doesn't understand is treated like an outage.
      return { action: 'proceed', status: 'unchecked' };
    }
    const result = data;
    const choice = await new Promise<AddressChoice>((resolve) => {
      addressChoiceRef.current = resolve;
      setAddressReview({ entered, result });
    });
    addressChoiceRef.current = null;
    setAddressReview(null);
    if (choice === 'edit') return { action: 'stay' };
    if (choice === 'use_suggested' && result.normalized) {
      return { action: 'proceed', status: 'confirmed', suggested: result.normalized };
    }
    return { action: 'proceed', status: 'as_entered' };
  }, [collectEnteredAddress]);

  // ── Navigation ──
  // QA fix wave (P1 "Back dead-ends at step 3"): DisclosureGate auto-resolves
  // an ALREADY-accepted consent while rendering nothing — with a forward-only
  // resolve, Back onto an accepted gate instantly bounced forward again,
  // stranding every screen before it. Track travel direction so the gate's
  // auto-resolve continues in the direction the user was going.
  const navDirectionRef = useRef<'forward' | 'back'>('forward');

  // #2448: every step opens at its top. The page scrolls inside MainLayout's
  // <main>, not the window, so a long step's scroll position carried over and
  // Save and continue landed the family mid-way down the next step.
  const firstStepRef = useRef(true);
  useEffect(() => {
    if (firstStepRef.current) { firstStepRef.current = false; return; }
    document.getElementById('main-content')?.scrollTo?.({ top: 0 });
  }, [stepIndex]);

  // #2440: record the step the family is ON, so a sign-out, refresh or Back
  // reopens it. Fire-and-forget: a lost write only costs the old data-based
  // resume. Not re-sent for the step the draft reopened on.
  const appId = app?._id;
  const appStatus = app?.status;
  useEffect(() => {
    if (loading || !appId || !dataSource.saveResumeStep) return;
    if (appStatus !== 'draft' && appStatus !== 'needs_more_info') return;
    // #2199: the notice is not a step. Recording the hidden step would reopen
    // the walk past a Region answer that was never saved.
    if (canadaStop) return;
    const current = STEPS[stepIndex];
    if (!current) return;
    const id = stepId(current);
    if (!RESUME_STEP_ID.test(id) || id === savedResumeStepRef.current) return;
    savedResumeStepRef.current = id;
    void Promise.resolve()
      .then(() => dataSource.saveResumeStep?.(appId, id))
      .catch(() => {});
  }, [loading, appId, appStatus, stepIndex, STEPS, dataSource, canadaStop]);

  /**
   * #2528: the step's own validation toast ("2 questions need attention").
   * It used to live its global 7 s — longer whenever Radix paused the timer —
   * and stayed over steps 6 and 7 after the family fixed step 5 and moved on.
   * Kept by handle and dismissed on ANY step change: Save and continue, Back,
   * a jump from the step list, or the empty-screen walk-through.
   */
  const stepToastRef = useRef<{ dismiss: () => void } | null>(null);
  useEffect(() => {
    stepToastRef.current?.dismiss();
    stepToastRef.current = null;
  }, [stepIndex]);
  const raiseStepToast = useCallback((props: Parameters<typeof toast>[0]) => {
    stepToastRef.current?.dismiss();
    stepToastRef.current = toast(props) ?? null;
  }, [toast]);

  /** #2527: the furthest step this visit has reached — the step list opens
   *  any step up to it. */
  const [furthestStep, setFurthestStep] = useState(0);
  useEffect(() => {
    if (!loading) setFurthestStep((f) => Math.max(f, stepIndex));
  }, [loading, stepIndex]);
  /** While TRF waits on the family every step is open: the whole walk was
   *  completed once, and the asked-about answers can sit anywhere in it.
   *  TRF ruled this as built on 2026-10-16 (wizard-step-jump-rule). */
  const canJumpTo = useCallback(
    (i: number) => !saving && (appStatus === 'needs_more_info' || i <= furthestStep),
    [saving, appStatus, furthestStep],
  );
  /**
   * #2948 (b): a step opened from Review returns there. A family fixing one
   * answer from Review used to land on the NEXT step after Save & continue
   * and walk the rest again. Set when a jump leaves the Review step; the next
   * forward move goes back to it, and reaching Review any way clears it.
   */
  const returnToReviewRef = useRef<number | null>(null);
  useEffect(() => {
    if (STEPS[stepIndex]?.type === 'submit_consent') returnToReviewRef.current = null;
  }, [STEPS, stepIndex]);
  const jumpTo = useCallback((i: number) => {
    if (i === stepIndex || i < 0 || i >= STEPS.length) return;
    if (STEPS[stepIndex]?.type === 'submit_consent' && i < stepIndex) returnToReviewRef.current = stepIndex;
    // The direction decides how an empty screen or an accepted consent
    // passes the family through, exactly as Back and Continue do.
    navDirectionRef.current = i < stepIndex ? 'back' : 'forward';
    setStepIndex(i);
  }, [stepIndex, STEPS]);

  const goNext = useCallback(() => {
    navDirectionRef.current = 'forward';
    const review = returnToReviewRef.current;
    if (review !== null) {
      returnToReviewRef.current = null;
      setStepIndex(review);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    navDirectionRef.current = 'back';
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  /**
   * #1639 / D-FB-11: a screen every one of whose questions is hidden is not
   * shown — it is walked THROUGH, in the direction the family was travelling.
   *
   * A step with a heading and a Continue button and nothing to answer reads as
   * a broken form; the builder preview already omits it, and this is the wizard
   * keeping that promise. Deliberately a pass-through and not a change to
   * `STEPS`: the step list is the walk this application was started against and
   * `stepIndex` indexes it, so removing a step mid-walk would slide every later
   * index under a family standing on one.
   *
   * The direction of travel is the PREFERENCE, not the whole rule. Travelling
   * BACK off the start of the list, the search found nothing below index 0 and
   * moved nobody — which parked the family on exactly the blank screen this
   * exists to prevent, with a Back button already disabled at index 0 and a
   * Continue that walks them straight back onto it. So a search that runs off
   * one end turns round and looks the other way: the family is never left
   * standing on a screen with nothing on it while a screen with questions
   * exists. Only when NOTHING in either direction has anything to ask does
   * nothing move, and that is a form with no questions in it at all.
   */
  const screenIsEmpty = useCallback(
    (step: StepKind | undefined) => step?.type === 'screen' && askedGroups(step.screen).length === 0,
    [askedGroups],
  );
  useEffect(() => {
    if (!screenIsEmpty(STEPS[stepIndex])) return;
    /** The first step with something on it, walking `dir` from here. */
    const seek = (dir: 1 | -1): number | null => {
      let i = stepIndex + dir;
      while (i >= 0 && i < STEPS.length && screenIsEmpty(STEPS[i])) i += dir;
      return i >= 0 && i < STEPS.length ? i : null;
    };
    const dir: 1 | -1 = navDirectionRef.current === 'back' ? -1 : 1;
    const landing = seek(dir) ?? seek(dir === 1 ? -1 : 1);
    if (landing !== null) setStepIndex(landing);
  }, [STEPS, stepIndex, screenIsEmpty]);

  /**
   * #2197: the client half of the mandatory-documents gate. True (and the
   * family is told which ones, by name) when a required slot is still empty.
   * Runs on Continue from the documents step AND before the submit call, so a
   * family who resumes past the step is caught too; the server refuses either
   * way, this just says so before the consent box rather than after it.
   */
  const blockOnMissingDocuments = useCallback((): boolean => {
    const missing = missingRequiredSlots(docSlots, uploadedDocs);
    if (missing.length === 0) {
      setMissingDocKinds((prev) => (prev.length ? [] : prev));
      return false;
    }
    // #2377: each missing tile also says so in place, and takes focus.
    setMissingDocKinds(missing.map((s) => s.kind));
    toast({
      title: t('forms:documents.requiredMissingTitle'),
      description: t('forms:documents.requiredMissingBody', {
        slots: formatList(missing.map((s) => s.label), { type: 'conjunction' }),
      }),
      variant: 'destructive',
    });
    return true;
  }, [docSlots, uploadedDocs, toast, t]);

  const handleSaveAndContinue = useCallback(async (step: StepKind) => {
    if (step.type === 'screen') {
      // 2027 gate: block advance while any required field on THIS screen is
      // empty, OR any filled field has a range/format problem (QA fix wave).
      // A bad VALUE always blocks. Emptiness blocks only where the family
      // could actually have answered: #1219 — `intake.residence_state` IS
      // required and IS a conditional follow-up, so "conditional follow-ups
      // are never required" was never a property of the model, only of one
      // seed. When its driver resolves to no branch the control renders
      // disabled and demanding it strands the family with no way out, so
      // `hasAnythingToPick` (the client half of the server's identically named
      // predicate) decides whether emptiness is the family's doing.
      // #1639: the questions this family was ASKED, not every question the
      // screen lists. A `visible_when` follow-up whose driver did not reveal it
      // is not on screen, so demanding it stranded the family on a page with no
      // way forward — and the server's own submit gate
      // (`publishedForms.missingRequiredFields`) never demanded it either, so
      // the two disagreed about whether the application was complete.
      const refs = askedGroups(step.screen).flatMap((g) => g.fields);
      const problems = new Map<string, string>();
      for (const ref of refs) {
        const spec = findFieldSpec(ref.section, ref.key);
        if (!spec) continue;
        const value = get(sectionData[ref.section], ref.key);
        const answerable = hasAnythingToPick(
          spec,
          sectionData[ref.section],
          (key) => findFieldSpec(ref.section, key),
        );
        // #2586: a half-typed date stores null, which reads as "empty". The
        // control's own report wins: Save stays on the step and names the
        // problem at the date, whether the date is required or optional, so a
        // part-typed optional date is never dropped silently.
        const dateDraftProblem = spec.type === 'date' ? dateDraftsRef.current.get(ref.key) : undefined;
        if (dateDraftProblem) {
          problems.set(`${ref.section}.${ref.key}`, dateDraftProblem);
          continue;
        }
        if (spec.required && isFieldValueEmpty(spec, value) && answerable) {
          problems.set(`${ref.section}.${ref.key}`, resolveMessage({ key: 'field.required' }));
          continue;
        }
        const problem = fieldValueProblemRequest(spec, value);
        if (problem) problems.set(`${ref.section}.${ref.key}`, resolveMessage(problem));
      }
      // #1111 / #1838 — dates that cannot be true. Cross-field, so it cannot
      // live in `fieldValueProblem`, which sees one spec and one value:
      // "diagnosis before birth" needs both. Merged only for the controls
      // actually on THIS page, so a bad date on the patient screen does not
      // surface as an unreachable error while the family is on the finances one.
      const onThisPage = new Set(refs.map((ref) => `${ref.section}.${ref.key}`));
      for (const [path, message] of dateRuleProblems(dateRules, sectionData)) {
        if (onThisPage.has(path)) problems.set(path, message);
      }
      if (problems.size > 0) {
        setMissingFields(problems);
        raiseStepToast({
          title: tv('summary.title'),
          description: tv('summary.description', { count: problems.size }),
          variant: 'destructive',
        });
        const firstKey = [...problems.keys()][0].split('.').slice(1).join('.');
        // Optional-call: jsdom (tests) doesn't implement scrollIntoView.
        document.getElementById(`intake-field-${firstKey.replace(/\./g, '-')}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return;
      }
      // #1059: validate the contact screen's address group BEFORE the save.
      // Mode-gated server-side (off answers { mode:'off' } and nothing below
      // changes); 'corrected'/'unknown' open the review dialog and the await
      // resolves with the family's choice. Suggest, never block.
      const overrides: Partial<Record<SectionKey, Record<string, unknown>>> = {};
      if (step.screen.id === 'contact') {
        const outcome = await resolveAddressGroup();
        if (outcome.action === 'stay') return;
        let nextHousehold = sectionData.household;
        let nextIntake = sectionData.intake;
        // #1055: the wizard writes these paths on the family's behalf, so it
        // declares them dirty itself — a save carries dirty paths only, and an
        // undeclared write would simply never leave the tab.
        if (outcome.suggested) {
          nextHousehold = setPath(nextHousehold, 'address.street', outcome.suggested.street);
          nextHousehold = setPath(nextHousehold, 'address.apt', outcome.suggested.apt);
          nextHousehold = setPath(nextHousehold, 'address.city', outcome.suggested.city);
          nextHousehold = setPath(nextHousehold, 'address.zip', outcome.suggested.zip);
          markDirty('household', 'address.street', 'address.apt', 'address.city', 'address.zip');
          if (outcome.suggested.state) {
            nextIntake = setPath(nextIntake, 'residence_state', outcome.suggested.state);
            markDirty('intake', 'residence_state');
          }
        }
        if (outcome.status) {
          nextHousehold = setPath(nextHousehold, 'address.verification', {
            status: outcome.status,
            checked_at: new Date().toISOString(),
          });
          markDirty('household', 'address.verification');
        }
        if (nextHousehold !== sectionData.household) overrides.household = nextHousehold;
        if (nextIntake !== sectionData.intake) overrides.intake = nextIntake;
        if (overrides.household || overrides.intake) {
          setSectionData((prev) => ({
            ...prev,
            ...(overrides.household ? { household: overrides.household } : {}),
            ...(overrides.intake ? { intake: overrides.intake } : {}),
          }));
        }
      }
      try {
        await Promise.all(screenSections(step.screen).map((sec) => saveSection(sec, overrides[sec] ? { data: overrides[sec] } : {})));
        setMissingFields(new Map());
        goNext();
      } catch {
        // Error already toasted in saveSection; stay on step
      }
    } else {
      if (step.type === 'documents' && blockOnMissingDocuments()) return; // #2197
      goNext();
    }
  }, [saveSection, goNext, sectionData, raiseStepToast, resolveAddressGroup, markDirty, findFieldSpec, askedGroups, dateRules, resolveMessage, tv, blockOnMissingDocuments]);

  /**
   * D-FB-12: upload the answer to a builder-minted `file` question.
   *
   * (The fallback sentence below is `UPLOAD_FAILED_FALLBACK`, declared once at
   * module scope — both upload paths say the same thing to a family, and the
   * #1039 covered-surface guard counts each hardcoded occurrence separately.)
   *
   * Separate from `handleUpload` above, which is the code-pinned document-slots
   * path: that one is addressed by kind, accumulates into `uploadedDocs`, and
   * honours per-kind caps. This one is addressed by FIELD id and its answer
   * lives in the section data like every other answer, because that is what
   * makes it a field rather than a slot.
   *
   * The server writes the answer itself and returns the reference; this only
   * mirrors it into local state so the control names the file without a
   * refetch. Errors are re-thrown rather than toasted — the control renders
   * the message inline, next to the question it belongs to.
   */
  const uploadFieldFile = useCallback(async (applicationId: string, fieldKey: string, file: File) => {
    const fd = new FormData();
    fd.append('document', file);
    try {
      const res = await dataSource.uploadFieldDocument!(applicationId, fieldKey, fd);
      return { source_document_id: res.data!.id, filename: res.data!.filename };
    } catch (err) {
      throw new Error(englishServerMessage(err, i18n.language ?? 'en') || t(UPLOAD_FAILED_FALLBACK_KEY), { cause: err });
    }
  }, [dataSource, i18n, t]);

  // ── Document upload ──
  const handleUpload = useCallback(async (kind: string, file: File, opts?: UploadOptions): Promise<UploadOutcome> => {
    if (!app) return { ok: false, error: t(UPLOAD_FAILED_FALLBACK_KEY) };
    const isMulti = !!docSlots.find((s) => s.kind === kind)?.multiple;
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('document', file);
    // #1425: only the tile's confirmed single-slot Replace sets this. Without
    // it the server has no way to tell a deliberate swap from a scripted
    // double-POST, so it refuses at the 1-per-application cap (#1386/#1406) and
    // the family's confirmed replace came back as a destructive red toast. With
    // it, the server stores the new document and only then supersedes the old.
    if (opts?.replace) fd.append('replace', 'true');
    try {
      const res = await dataSource.uploadDocument(app._id, fd, opts?.onProgress);
      // #2471: these exact bytes were already in the slot — nothing new was
      // stored, so nothing is added to the roster.
      if (res.data?.duplicate) {
        toast({
          title: t('forms:documents.duplicateTitle'),
          description: t('forms:documents.duplicateBody', { file: res.data?.filename || file.name }),
        });
        return { ok: true };
      }
      // #2477: the same file is also in another tile — allowed (one paper can
      // meet two requirements), but said, in case it was a mis-pick.
      if (res.data?.also_in) {
        const other = docSlots.find((s) => s.kind === res.data!.also_in)?.label ?? res.data.also_in;
        toast({ title: t('forms:documents.alsoInTitle'), description: t('forms:documents.alsoInBody', { file: file.name, slot: other }) });
      }
      // The upload answer names the file; the roster also shows its type, size
      // and who added it, which this browser already knows.
      const row: ApplicationDocumentMeta | undefined = res.data
        ? { mime_type: file.type, file_size: file.size, provenance: 'family', ...res.data }
        : undefined;
      if (row) {
        setUploadedDocs((prev) => {
          if (isMulti) {
            // F-8: multi-slot kinds (bill_statement) accumulate. F-2: appending
            // NEVER drops a prior upload — the root cause of the data-loss bug was
            // the unconditional `prev.filter((d) => d.kind !== kind)` below.
            return [...prev, row];
          }
          // Single-slot kind: a replace only reaches here after the
          // DocumentUploadStep confirm-before-replace guard (F-2), so swapping the
          // same-kind doc for the new one is user-confirmed, not silent.
          const without = prev.filter((d) => d.kind !== kind);
          return [...without, row];
        });
      }
      return { ok: true };
    } catch (err) {
      // QA fix wave: the backend returns the REAL, permanent reason ("Invalid
      // file type. Only JPEG, PNG, and PDF allowed." / "File is too large.").
      // #2477: it is shown on the file's own row, next to Retry, rather than in
      // a toast that disappears.
      // #2473: worded by kind (too large, wrong type, connection, failed) in
      // the family's language; a server sentence survives only for a 4xx the
      // catalog has no line for, and only for an English reader.
      return { ok: false, error: uploadFailureText(t, err, i18n.language ?? 'en') };
    }
  }, [app, toast, dataSource, docSlots, i18n, t]);

  // ── #2471: view, remove and undo on the documents roster ──
  const loadDocumentFile = useCallback(
    (doc: ApplicationDocumentMeta) => dataSource.documentFile!(app!._id, doc.id),
    [app, dataSource],
  );
  const handleViewDocument = useCallback((doc: ApplicationDocumentMeta) => {
    if (!app || !dataSource.documentFile) return;
    // Synchronous from the click: openAuthedDocument opens the tab before any
    // await so a popup blocker lets it through.
    void openAuthedDocument(() => dataSource.documentFile!(app._id, doc.id), { toast });
  }, [app, dataSource, toast]);

  const handleRestoreDocument = useCallback(async (doc: ApplicationDocumentMeta) => {
    if (!app || !dataSource.restoreDocument) return;
    try {
      await dataSource.restoreDocument(app._id, doc.id);
      setUploadedDocs((prev) => (prev.some((d) => d.id === doc.id) ? prev : [...prev, doc]));
    } catch (err) {
      toast({
        title: t('forms:documents.restoreFailedTitle'),
        description: englishServerMessage(err, i18n.language ?? 'en') || t(UPLOAD_FAILED_FALLBACK_KEY),
        variant: 'destructive',
      });
    }
  }, [app, dataSource, toast, i18n, t]);

  const handleRemoveDocument = useCallback(async (doc: ApplicationDocumentMeta) => {
    if (!app || !dataSource.removeDocument) return;
    try {
      await dataSource.removeDocument(app._id, doc.id);
    } catch (err) {
      toast({
        title: t('forms:documents.removeFailedTitle'),
        description: englishServerMessage(err, i18n.language ?? 'en') || t(UPLOAD_FAILED_FALLBACK_KEY),
        variant: 'destructive',
      });
      return;
    }
    setUploadedDocs((prev) => prev.filter((d) => d.id !== doc.id));
    // Undo, not "Are you sure?": removing is one click to reverse (NN/g).
    toast({
      title: t('forms:documents.removedTitle', { file: doc.filename }),
      action: (
        <ToastAction altText={t('forms:documents.undo')} onClick={() => void handleRestoreDocument(doc)}>
          {t('forms:documents.undo')}
        </ToastAction>
      ),
    });
  }, [app, dataSource, toast, i18n, t, handleRestoreDocument]);

  // ── Final submit ──
  const handleSubmit = useCallback(async () => {
    if (!app) return;
    // #2197: never send a submit the server will refuse for a missing
    // mandatory document — take the family back to the upload step instead.
    if (blockOnMissingDocuments()) {
      const documentsIndex = STEPS.findIndex((s) => s.type === 'documents');
      if (documentsIndex >= 0) setStepIndex(documentsIndex);
      return;
    }
    setSaving(true);
    try {
      // #1095 item 1: tell the server which version was ON SCREEN. `version` is
      // pinned at mount, so if TRF republished while this family was answering,
      // this is what keeps their answers validated and stamped against the
      // questions they were actually asked.
      const submitted = await dataSource.submit(app._id, { version_n: version.version_n });
      // #2332: write the submitted record into the cache the landing page
      // reads BEFORE navigating. With the 5-minute staleTime, /bills otherwise
      // painted the cached draft and offered "Continue your renewal" — a dead
      // end, because the wizard refuses a renewal that is no longer editable.
      await syncMineCache(queryClient, dataSource, submitted);
      // The submit succeeded: no step's validation toast is true any more.
      dismissRaisedToasts();
      // Renewal lands on the Grants page, whose renewal line confirms the
      // submitted request (#2331); initial lands on the application status page.
      navigate(flow === 'renewal' ? '/bills?tab=grants' : '/apply/status');
    } catch (err) {
      // #2199: the Canada switch refused the submit — the notice says so.
      if (isCanadaIntakeClosedError(err)) {
        setCanadaRefusedByServer(true);
        return;
      }
      // #2198: past the cycle's deadline "please try again" is false. The code
      // is machine-readable, so every language gets the deadline line; English
      // readers still see the server's own text (it may carry TRF's notice).
      const data = (err as { response?: { data?: { code?: string; details?: { missing_documents?: unknown; missing_fields?: unknown } } } })?.response?.data;
      const code = data?.code;
      // #2943: the server refused for a blank required answer (blanked after
      // its step was passed, e.g. from Review). Name each one in the family's
      // language, mark the field, and take the family to the first step that
      // holds one — the documents gate's shape below. Review stays unflagged.
      if (code === REQUIRED_ANSWERS_MISSING_CODE) {
        const paths = Array.isArray(data?.details?.missing_fields)
          ? (data.details.missing_fields as unknown[]).filter((p): p is string => typeof p === 'string')
          : [];
        const stepKeys = flagKeysByStep(STEPS);
        const named: Array<{ path: string; label: string; step: number }> = [];
        for (const path of paths) {
          const dot = path.indexOf('.');
          const section = path.slice(0, dot) as SectionKey;
          const spec = dot > 0 ? findFieldSpec(section, path.slice(dot + 1)) : undefined;
          if (!spec) continue;
          named.push({
            path,
            label: resolveFieldLabel(spec, sectionData[section], sectionData),
            step: stepKeys.findIndex((keys) => keys.includes(path)),
          });
        }
        if (named.length) {
          const message = resolveMessage({ key: 'field.required' });
          setMissingFields(new Map(named.map((n) => [n.path, message])));
        }
        toast({
          title: t('forms:wizard.requiredAnswersMissingTitle'),
          description: named.length
            ? t('forms:wizard.requiredAnswersMissingBody', {
              fields: formatList(named.map((n) => n.label), { type: 'conjunction' }),
            })
            : englishServerMessage(err, i18n.language ?? 'en') || t('forms:wizard.submitFailedBody'),
          variant: 'destructive',
        });
        const steps = named.map((n) => n.step).filter((i) => i >= 0);
        if (steps.length) {
          navDirectionRef.current = 'back';
          setStepIndex(Math.min(...steps));
        }
        return;
      }
      // #2197: the server refused for missing mandatory documents (the client
      // list was stale or unread). Name them in the family's language, mark
      // their tiles, and take the family back to the upload step.
      if (code === REQUIRED_DOCUMENTS_MISSING_CODE) {
        const kinds = Array.isArray(data?.details?.missing_documents)
          ? (data.details.missing_documents as unknown[]).filter((k): k is string => typeof k === 'string')
          : [];
        const named = docSlots.filter((s) => kinds.includes(s.kind));
        setMissingDocKinds(named.map((s) => s.kind));
        toast({
          title: t('forms:documents.requiredMissingTitle'),
          description: named.length
            ? t('forms:documents.requiredMissingBody', {
              slots: formatList(named.map((s) => s.label), { type: 'conjunction' }),
            })
            : englishServerMessage(err, i18n.language ?? 'en') || t('forms:wizard.submitFailedBody'),
          variant: 'destructive',
        });
        const documentsIndex = STEPS.findIndex((s) => s.type === 'documents');
        if (documentsIndex >= 0) {
          navDirectionRef.current = 'back';
          setStepIndex(documentsIndex);
        }
        return;
      }
      const closed = code === CYCLE_CLOSED_CODE;
      toast({
        title: t(closed
          ? wordingFor(i18n, flow, 'forms:wizard.cycleClosedTitle', 'forms:wizard.cycleClosedTitleRenewal')
          : wordingFor(i18n, flow, 'forms:wizard.submitFailedTitle', 'forms:wizard.submitFailedTitleRenewal')),
        description: englishServerMessage(err, i18n.language ?? 'en') || t(closed
          ? wordingFor(i18n, flow, 'forms:wizard.cycleClosedBody', 'forms:wizard.cycleClosedBodyRenewal')
          : 'forms:wizard.submitFailedBody'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [app, navigate, queryClient, toast, dataSource, flow, version, i18n, t, blockOnMissingDocuments, STEPS, dismissRaisedToasts, docSlots, findFieldSpec, sectionData, resolveMessage]);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <MonoLabel className="animate-pulse">
          {t(wordingFor(i18n, flow, 'forms:wizard.loading', 'forms:wizard.loadingRenewal'))}
        </MonoLabel>
      </div>
    );
  }

  // #2850: a stop on the page, never the server's English sentence.
  if (renewalNotEligible) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-14 space-y-4" data-testid="renewal-not-eligible">
        <h1 className="text-title font-normal leading-tight text-foreground font-serif">
          {t('forms:wizard.renewalNotEligibleTitle')}
        </h1>
        <p className="text-body text-stevens-ink-2">{t('forms:wizard.renewalNotEligibleBody')}</p>
        <p className="text-body">
          <Link
            to="/apply"
            className="text-stevens-ink underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo hover:decoration-stevens-indigo"
          >
            {t('forms:wizard.renewalNotEligibleApply')}
          </Link>
        </p>
      </div>
    );
  }

  // #2687: renewals stop at Year 3. A stop on the page, never the server's sentence.
  if (renewalYearCap) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-14 space-y-4" data-testid="renewal-year-cap">
        <h1 className="text-title font-normal leading-tight text-foreground font-serif">
          {t('forms:wizard.renewalYearCapTitle')}
        </h1>
        <p className="text-body text-stevens-ink-2">{t('forms:wizard.renewalYearCapBody')}</p>
      </div>
    );
  }

  // #2792 follow-up: the eligibility miss on the create, as a stop on the page
  // rather than a "refresh and retry" toast over a blank wizard. The server's
  // sentence (English readers only, as everywhere in this file), then how to
  // reach TRF (#3306: there is no appeal).
  if (prescreenMiss) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-14 space-y-4" data-testid="prescreen-miss">
        <h1 className="text-title font-normal leading-tight text-foreground font-serif">
          {t('forms:wizard.startFailedTitle')}
        </h1>
        {prescreenMiss.message ? <p className="text-body text-stevens-ink-2">{prescreenMiss.message}</p> : null}
        <p className="text-body text-stevens-ink">
          {t('forms:wizard.prescreenMissContact', { email: TRF_CONTACT_EMAIL, phone: TRF_CONTACT_PHONE })}
        </p>
      </div>
    );
  }

  // Wave launch-w1014 L3-a: the create refused. Title, the reason in the
  // family's language, a code box that starts the application again (not for
  // a closed round: no code opens it), and where to get help.
  if (startRefused) {
    const closedRound = startRefused.reason === 'cycle-closed';
    const reasonText = closedRound
      // A staff-written closed notice is English only (R4-B17), as on /apply.
      ? (startRefused.message || t('forms:wizard.startRefused.cycleClosed'))
      : startRefused.reason === 'referral'
        ? t('forms:wizard.startRefused.referral')
        : startRefused.reason === 'new-families'
          ? t('forms:wizard.startRefused.newFamiliesSoon', {
            date: startRefused.opensOn
              ? new Intl.DateTimeFormat(i18n.language || 'en', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
                .format(new Date(`${startRefused.opensOn}T12:00:00Z`))
              : '',
          })
          : (startRefused.message || t('forms:wizard.refreshAndRetry'));
    const retryWithCode = () => {
      // The code just typed is the credential now; a stashed invite token
      // would win over it on the server and be refused again.
      try { sessionStorage.removeItem(APPLY_INVITE_STORAGE_KEY); } catch { /* storage unavailable */ }
      setStartRefused(null);
      setLoading(true);
      setStartAttempt((n) => n + 1);
    };
    return (
      <div className="max-w-2xl mx-auto px-4 py-14 space-y-4" data-testid="start-refused">
        <h1 className="text-title font-normal leading-tight text-foreground font-serif">
          {t('forms:wizard.startRefused.title')}
        </h1>
        <p className="text-body text-stevens-ink-2">{reasonText}</p>
        {!closedRound && (
          <ApplicationCodeEntry
            key={startAttempt}
            heading={t('forms:wizard.startRefused.codeHeading')}
            onCodeAccepted={retryWithCode}
            defaultExpanded
          />
        )}
        <p className="text-body text-stevens-ink">
          {t('forms:wizard.startRefused.support')}{' '}
          <ExternalLink
            href="https://www.trf.org/get-support"
            className="text-stevens-ink underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo hover:decoration-stevens-indigo"
          >
            trf.org/get-support
          </ExternalLink>
        </p>
      </div>
    );
  }

  if (!app) return null;

  const step = STEPS[stepIndex];

  // ── Section field change helpers ──
  const updateField = (sectionKey: SectionKey, path: string, value: unknown) => {
    // #1059: the verification marker describes ONE checked address — editing
    // any field of the address group (street/apt/city/zip here; state and
    // country on intake) makes it stale, so it's stripped the moment the
    // value changes. The next contact-screen advance re-validates and writes
    // a fresh one (or none, while the mode is off).
    const touchesAddress =
      (sectionKey === 'household' && path.startsWith('address.') && path !== 'address.verification')
      || (sectionKey === 'intake' && (path === 'residence_state' || path === 'country'));
    // Decided from the CURRENT snapshot rather than inside the updater, so the
    // dirty bookkeeping below is a plain statement and not a side effect React
    // is free to run twice.
    const stripsVerification = touchesAddress
      && ((sectionData.household?.address as Record<string, unknown> | undefined)?.verification ?? null) !== null;

    // #2523 follow-up: changing Region carries the answers already given on
    // the other country's lists onto the new one, band for band, rather than
    // leaving "miles" under a kilometre question. Decided from the current
    // snapshot, like the verification strip below.
    const carried = sectionKey === 'intake' && path === 'country'
      ? alignCountryAnswers({ ...sectionData, intake: setPath(sectionData.intake, path, value) }, findFieldSpec)
      : null;

    setSectionData((prev) => {
      let next = { ...prev, [sectionKey]: setPath(prev[sectionKey], path, value) };
      for (const [section, key] of carried?.moved ?? []) {
        next = { ...next, [section]: setPath(next[section], key, get(carried!.sections[section], key)) };
      }
      for (const r of carried?.repick ?? []) next = { ...next, [r.section]: setPath(next[r.section], r.key, '') };
      if (touchesAddress) {
        const household = next.household as Record<string, unknown>;
        const address = household?.address as Record<string, unknown> | undefined;
        if (address && address.verification != null) {
          const rest = { ...address };
          delete rest.verification;
          next = { ...next, household: { ...household, address: rest } };
        }
      }
      return next;
    });
    // #1055: this edit, and only this edit, is what the next save carries. The
    // stripped marker is a write too — a dotted `$set` has no "unset", so it
    // rides as an explicit null (see takeDirty).
    markDirty(sectionKey, path);
    for (const [section, key] of carried?.moved ?? []) markDirty(section, key);
    // A Region change re-derives the re-pick notes; answering a noted question clears its note.
    if (carried) setRepicks(new Map(carried.repick.map((r) => [`${r.section}.${r.key}`, r])));
    else if (repicks.has(`${sectionKey}.${path}`)) {
      setRepicks((prev) => { const m = new Map(prev); m.delete(`${sectionKey}.${path}`); return m; });
    }
    if (stripsVerification) markDirty('household', 'address.verification');
    // A flagged field clears its marker only when the new value actually
    // resolves the problem — whitespace into a required field, or a value
    // still out of range, keeps the flag (QA fix wave).
    const flagKey = `${sectionKey}.${path}`;
    setMissingFields((prev) => {
      if (!prev.has(flagKey)) return prev;
      const spec = findFieldSpec(sectionKey, path);
      // #1219: same predicate as the advance gate, so a marker can never
      // outlive the condition that set it. The driver is a sibling other than
      // `path`, so the pre-update `sectionData` still holds its current value.
      const stillBad = spec
        ? (spec.required
            && isFieldValueEmpty(spec, value)
            && hasAnythingToPick(spec, sectionData[sectionKey], (key) => findFieldSpec(sectionKey, key)))
          || fieldValueProblem(spec, value) !== null
        : false;
      if (stillBad) return prev;
      const next = new Map(prev);
      next.delete(flagKey);
      return next;
    });
  };

  const blurSave = (sectionKey: SectionKey) => {
    // Fire-and-forget on blur — silent so it never disables the nav buttons.
    // #2727: the blur save carries what the pause timer would have.
    cancelAutosave(sectionKey);
    // #2941: like the pause autosave, a section with nothing unsaved sends
    // nothing. A no-change blur used to PATCH and flip the save label, which
    // reflowed the phone header and moved "Save & continue" mid-click.
    if (isClean(sectionKey)) return;
    saveSection(sectionKey, { silent: true }).catch(() => {});
  };

  // B-162: schedule a post-commit silent save for a click-committed control.
  const flagClickSave = (target: SectionKey) => {
    setClickSave((s) => ({ target, nonce: s.nonce + 1 }));
  };

  // #2527: what TRF asked about, while it waits. Each item names the
  // question (or document) and the step that holds it; an asked-about
  // question this version no longer asks is on no step and is left out — the
  // family cannot answer it, and staff see it flagged as invisible.
  const moreInfo = app.status === 'needs_more_info';
  const flaggedSet = new Set(moreInfo ? app.flagged_fields ?? [] : []);
  const stepKeys = flagKeysByStep(STEPS);
  const askedItems: Array<{ key: string; label: string; step: number }> = [];
  if (moreInfo) {
    for (const flag of app.flagged_fields ?? []) {
      const stepAt = stepKeys.findIndex((keys) => keys.includes(flag));
      const dot = flag.indexOf('.');
      const spec = dot > 0 ? findFieldSpec(flag.slice(0, dot), flag.slice(dot + 1)) : undefined;
      // #2523 follow-up: named the way the step words it for this family
      // ("kilometres" for Canada), not by the base label.
      const label = spec ? resolveFieldLabel(spec, sectionData[flag.slice(0, dot) as SectionKey], sectionData) : undefined;
      if (stepAt >= 0 && label) askedItems.push({ key: flag, label, step: stepAt });
    }
    const documentsAt = stepKeys.findIndex((keys) => keys.includes(DOCUMENTS_STEP_KEY));
    for (const d of app.flagged_documents ?? []) {
      const label = docSlots.find((slot) => slot.kind === d.kind)?.label ?? documentKindLabel(d.kind);
      if (documentsAt >= 0) askedItems.push({ key: `document:${d.kind}`, label, step: documentsAt });
    }
  }

  return (
    // #2586: date controls report their half-typed state to the Save gate.
    <DateDraftContext.Provider value={dateDraftRegistry}>
    {/* #2448: the step and its progress side by side from `lg` up — progress
        and the save state in the right-hand column, out of the form's space; on
        a phone the same block is one strip above the step. */}
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_13rem] lg:gap-x-8">
      <aside className="px-4 pb-4 lg:col-start-2 lg:row-start-1 lg:px-0 lg:pb-0">
        <div className="lg:sticky lg:top-0">
          <WizardProgress
            steps={WIZARD_STEPS}
            renewal={flow === 'renewal'}
            currentIndex={stepIndex}
            onSelect={jumpTo}
            canSelect={canJumpTo}
            // #2727: "Saved ✓" only while nothing typed since is waiting.
            // #2754 review: "Saving…" only while a save is on the wire; an edit
            // waiting on the pause timer, or whose save failed, shows no claim
            // at all (the failure toast already said so).
            status={saving || savesInFlight > 0 ? (
              <MonoLabel className="block animate-pulse">{t('forms:wizard.saving')}</MonoLabel>
            ) : savedAt && !unsaved ? (
              <MonoLabel tone="indigo" className="block">{t('forms:wizard.savedJustNow')}</MonoLabel>
            ) : null}
          />
        </div>
      </aside>
    <div className={`${WIZARD_STEP_CONTAINER} lg:col-start-1 lg:row-start-1`}>
      {moreInfo && (
        <MoreInfoBanner
          message={app.more_info_message ?? null}
          items={askedItems}
          onOpen={jumpTo}
          english={uiLocale === 'en'}
        />
      )}

      {/* #2199: the Canada stop takes the step's place, navigation included. */}
      {canadaStop ? (
        <CanadaIntakeClosedNotice
          onChangeAnswer={renewalOnRecordCanada || regionStepIndex < 0 ? undefined : () => {
            navDirectionRef.current = 'back';
            setStepIndex(regionStepIndex);
          }}
        />
      ) : (<>
      {/* ── Step content ── */}
      {step.type === 'screen' && (() => {
        const { screen } = step;
        return (
          <div className="space-y-6">
            <div className="space-y-1">
              {/* Grammar v3.3 (2026-08-27): eyebrows are authored AND rendered
                  without a dash — face, size and colour already carry the label
                  register, so the dash was only decorating it. This one stays on
                  the 11px label rung: it names the step beside the h1 rather
                  than heading the content, which is the eyebrow job. */}
              <MonoLabel tone="indigo">
                {screen.eyebrow}
              </MonoLabel>
              {staleMark(`screen.${screen.id}.eyebrow`)}
              {/* #2448: one line at the title rung. The pre-screen's 28–40px
                  display h1 took three lines of the form's first screenful
                  here, on every step. */}
              <h1 className="font-serif text-title font-normal leading-snug tracking-tight text-foreground">
                {screen.title}
                {staleMark(`screen.${screen.id}.title`)}
              </h1>
            </div>
            {flow === 'renewal' && screen.id === 'region_cycle' && (() => {
              // The renewal NAMES ITS YEAR (08-19 call). Live form state, not
              // the stored draft: application_type renders on this very screen
              // and the callout must follow the select. The wizard seeds
              // 'renewal_year_2' for renewals, so a year is normally present;
              // the generic fallback covers a family who picks "New applicant"
              // on the select despite being in the renewal flow.
              const yearN = renewalYearNumber(sectionData.intake?.application_type);
              return (
                <div className="rounded-lg border border-stevens-indigo/30 bg-stevens-indigo/5 p-4 text-body text-foreground">
                  {/* #2431: at 390 px this paragraph was one of four blocks
                      stacked above the first question. Below md the title
                      stands alone with a Details toggle; md and up unchanged. */}
                  <div className="flex items-start justify-between gap-3">
                    <p className="m-0 font-medium">
                      {yearN
                        ? t('forms:wizard.renewal.titleYear', { year: t('grants:renewal.year', { n: yearN }) })
                        : t('forms:wizard.renewal.titleGeneric')}
                    </p>
                    <span className="flex shrink-0 items-center gap-1.5 md:hidden">
                    <button
                      type="button"
                      aria-expanded={renewalDetailsOpen}
                      aria-controls={renewalDetailsId}
                      onClick={() => setRenewalDetailsOpen((open) => !open)}
                      /* 44 px on a touch screen without growing the card. */
                      className="inline-flex shrink-0 items-center rounded-sm text-body-sm text-stevens-indigo underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:-my-3 [@media(pointer:coarse)]:min-h-11 md:hidden"
                    >
                      {/* #3017: the guideline's disclosure swaps its word. */}
                      {renewalDetailsOpen ? t('common:actions.hideDetails') : t('common:actions.details')}
                    </button>
                    </span>
                  </div>
                  <p
                    id={renewalDetailsId}
                    className={`mt-1 text-stevens-ink-3${renewalDetailsOpen ? '' : ' hidden md:block'}`}
                  >
                    {t('forms:wizard.renewal.body')}
                  </p>
                </div>
              );
            })()}
            <div className="space-y-6">
              {/* #1639: the same `askedGroups` list the step gate reads. */}
              {askedGroups(screen).map((group, gi) => (
                <div key={gi} className="space-y-6">
                  {group.label && (
                    /* Grammar v3.3 (2026-08-27): this heads a group of questions
                       the family ANSWERS, so it is a heading — card-title Fraunces
                       on the ink rung — not an eyebrow on the 11px label rung.
                       The rule above every group after the first is layout doing
                       real work and stays; `block` went with the span. */
                    <h2
                      className={`text-card-title font-serif text-stevens-ink${gi > 0 ? ' border-t border-border pt-5' : ''}`}
                    >
                      {group.label}
                      {staleMark(`screen.${screen.id}.groups.${group.index}.label`)}
                    </h2>
                  )}
                  {group.fields.map((ref) => {
                    const field = findFieldSpec(ref.section, ref.key);
                    if (!field) return null;
                    const local = sectionData[ref.section];
                    const problem = missingFields.get(`${ref.section}.${ref.key}`);
                    // #2376: the control points at this marker while it shows.
                    const problemId = `intake-field-error-${ref.section}-${ref.key.replace(/\./g, '-')}`;
                    // #2527: a question TRF asked about carries its own note,
                    // apart from the red validation marker, for as long as TRF
                    // is waiting.
                    const askedAbout = moreInfo && flaggedSet.has(`${ref.section}.${ref.key}`);
                    return (
                      <div
                        key={`${ref.section}.${ref.key}`}
                        className={askedAbout ? 'border-s-2 border-status-warn-border ps-3' : undefined}
                        data-asked-about={askedAbout || undefined}
                      >
                        {askedAbout && (
                          <p className="mb-1 text-caption font-medium text-status-warn-fg">
                            {t('forms:wizard.moreInfo.fieldNote')}
                          </p>
                        )}
                        {repicks.has(`${ref.section}.${ref.key}`) && (() => {
                          const r = repicks.get(`${ref.section}.${ref.key}`)!;
                          return (
                            <p className="mb-1 text-caption font-medium text-status-warn-fg" data-testid={`repick-note-${ref.section}-${ref.key}`}>
                              {t(r.previousCountry === 'US' ? 'forms:wizard.repick.noteFromUS' : 'forms:wizard.repick.noteFromCA', { answer: repickPreviousLabel(r) })}
                            </p>
                          );
                        })()}
                        <IntakeFieldInput
                          field={field}
                          shownInEnglish={(slot) => staleKeys.has(`field.${ref.section}.${ref.key}.${slot}`)}
                          value={get(local, field.key)}
                          siblingValues={local}
                          // #602: the ethnicity taxonomy is driven by
                          // `intake.country`, which is not a sibling of a
                          // `patient` question.
                          sectionValues={sectionData}
                          onChange={(v) => {
                            updateField(ref.section, field.key, v);
                            noteEdit(ref.section);
                            // B-162: pills/checkbox commit on click, not blur — save now.
                            if (CLICK_COMMIT_TYPES.has(field.type)) flagClickSave(ref.section);
                          }}
                          onBlur={() => blurSave(ref.section)}
                          suggestedYear={ref.section === 'patient' && field.key === 'date_of_birth' ? prefillBirthYear : undefined}
                          invalid={!!problem}
                          errorId={problemId}
                          // #2737: a family that already exists does not pick its
                          // own renewal year — the server seeds it and only TRF
                          // changes it. Shown, not offered.
                          readOnly={flow === 'renewal' && isRenewalReadOnlyField(ref.section, field.key)}
                          // #2879 follow-up: a protection says why (D-SE-1).
                          readOnlyReason={flow === 'renewal' && isRenewalReadOnlyField(ref.section, field.key)
                            ? t('forms:wizard.renewalYearReadOnly')
                            : undefined}
                          decision={sectionData.intake?.country === 'CA' ? CANADA_FIELD_DECISIONS[field.key] : undefined}
                          onUploadFile={
                            // D-FB-12. Only offered when the flow actually has
                            // the route (applications, not renewals) — and only
                            // once a draft exists to attach the file to.
                            field.type === 'file' && app?._id && dataSource.uploadFieldDocument
                              ? (file) => uploadFieldFile(app._id, field.key, file)
                              : undefined
                          }
                        />
                        {problem && (
                          <p id={problemId} className="mt-1 text-caption text-status-danger-fg" role="alert">
                            {problem}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {step.type === 'consent' && (() => {
        // #1636: this is the branch that had to know something it did not.
        // Only the health-information step differs by country, and only for a
        // CA applicant — everything else below is untouched.
        const caHealthGate = step.documentId === 'phi_authorization'
          && sectionData.intake?.country === 'CA';

        // NOT KNOWN YET. The old code rendered the US gate here, so a family who
        // arrived while the index was still in flight could accept the wrong
        // document before the answer landed — and that acceptance is permanent.
        if (caHealthGate && (pipedaLookup === 'idle' || pipedaLookup === 'loading')) {
          return (
            <div
              data-testid="ca-consent-checking"
              role="status"
              className="rounded-lg border border-stevens-rule bg-stevens-bg-2 p-4"
            >
              <MonoLabel>{chrome.regionLoadingEyebrow}</MonoLabel>
              <p className="m-0 mt-1 text-body text-stevens-ink-2">
                {chrome.regionLoadingBody}
              </p>
            </div>
          );
        }

        // NOT KNOWN AT ALL. An error surface, deliberately not a consent one:
        // it says the page failed to load and offers the two things a family
        // needs — another attempt, and the way back to the questions. It does
        // NOT restate, summarise or stand in for the document, because the
        // wording of a consent gate is counsel's (copyLocks CONSENT_PHRASES),
        // and a step nobody could load has nothing to say about what it asks.
        //
        // Held, not closed: this is the one step of the walk that refuses to
        // guess, and it refuses in a way the family can act on. Failing closed
        // with no way out would cost more than the bug — a Canadian family who
        // cannot apply is one TRF never hears from.
        if (caHealthGate && pipedaLookup === 'error') {
          return (
            <div
              data-testid="ca-consent-unavailable"
              role="region"
              /* Labelled BY the headline rather than by a second copy of it: an
                 `aria-label` would have been a duplicate sentence that no copy
                 edit could reach, so a reworded headline would leave a screen
                 reader announcing the old one. */
              aria-labelledby="ca-consent-unavailable-title"
              className="space-y-4 rounded-lg border border-stevens-rule bg-stevens-bg-2 p-4"
            >
              <MonoLabel tone="ink">{chrome.regionErrorEyebrow}</MonoLabel>
              {/* Grammar §2: the card-title rung, the same one this file's group
                  headings use — not a raw Tailwind step. Serif is allowed here
                  because a card heading is one of its two sanctioned levels
                  (DECISIONS 2026-08-12). */}
              <h2 id="ca-consent-unavailable-title" className="text-card-title font-serif text-stevens-ink">
                {chrome.regionErrorTitle}
              </h2>
              <p className="m-0 text-body text-stevens-ink-2">
                {chrome.regionErrorBody}
              </p>
              {/* ONE new affordance, not two. The way back is the step's own
                  ghost "Back", rendered below by the same block that renders it
                  on every other consent step; a second ghost button here put
                  two retreats on one family-facing screen and made the family
                  choose between them. Only the retry is new, because only the
                  retry is something this step could not already do. */}
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" onClick={() => setPipedaAttempt((n) => n + 1)}>
                  {chrome.regionErrorRetry}
                </Button>
              </div>
            </div>
          );
        }

        // #329/D-7: the ONLY branch point. country==='CA' AND a published
        // pipeda doc exists → swap in the PIPEDA-framed document id + copy.
        // Every other case (US, unset, or CA pre-publication) is identical
        // to pre-#329 behavior — the same phi_authorization step/copy.
        const isCaPipeda = caHealthGate && pipedaLookup === 'published';
        // #1009: the CA branch reads the SAME chrome slots as the US step —
        // the eyebrow as-is, the title with the pinned suffix — so a staff
        // edit follows both branches and neither can drift.
        const copy = isCaPipeda
          ? { eyebrow: step.eyebrow, title: t('forms:wizard.canadaTitle', { title: step.title }), ...PIPEDA_CONSENT_COPY }
          : step;
        return (
          <>
          {/* R2-B12: the consent stays in English and authoritative; one line
              in the reader's language says so, before they read it. */}
          <DisclosureGate
            notice={<ConsentEnglishNotice show={uiLocale !== 'en'} className="m-0 text-body-sm leading-relaxed text-stevens-ink-3" />}
            documentId={isCaPipeda ? 'pipeda_authorization' : step.documentId}
            eyebrow={copy.eyebrow}
            title={copy.title}
            explanation={copy.explanation}
            consentLabel={copy.consentLabel}
            optional={step.optional}
            onAccepted={goNext}
            // Already-recorded consent self-resolves in the TRAVEL direction —
            // arriving via Back passes through to the previous step instead of
            // bouncing forward (QA fix wave, P1).
            onAlreadyAccepted={navDirectionRef.current === 'back' ? goBack : goNext}
            onSkip={step.optional ? goNext : undefined}
          />
          </>
        );
      })()}

      {step.type === 'documents' && (
        <DocumentUploadStep
          slots={docSlots}
          uploaded={uploadedDocs}
          onUpload={handleUpload}
          onView={dataSource.documentFile ? handleViewDocument : undefined}
          // #2471: removable only on a draft — the server refuses anything else.
          onRemove={dataSource.removeDocument && app?.status === 'draft' ? handleRemoveDocument : undefined}
          loadFile={dataSource.documentFile ? loadDocumentFile : undefined}
          missingKinds={missingDocKinds}
          renewal={flow === 'renewal'}
        />
      )}

      {step.type === 'submit_consent' && (
        <div className="space-y-6">
          {/* #1114 (TRF 2026-08-29, A2): the step is called "Review & submit"
              and now contains a review. It sits ABOVE the gate on purpose —
              below it, a family that ticks the box on sight would submit ~90
              answers it never got the chance to re-read.

              The screens come from `STEPS`, which was built from the version
              pinned at mount and stamped on the submission, so the summary is
              the questions this application is ACTUALLY being made against
              rather than the ones TRF publishes today. `retiredFields` carries
              the half the walk drops, so an answer given before a question was
              retired is read back rather than silently withheld from the family
              that typed it. Nothing here is derived or staff-owned: the
              component reads `sectionData` and nothing else. */}
          <ReviewSummary
            /* #1639: the read-back is of the walk this family took, so it shows
               the questions they were ASKED — the same `askedGroups` list the
               renderer and the step gate use. A hidden question read back as a
               blank line reads as a gap the family left, on a page whose whole
               job is letting them spot one. Their stored answer is untouched
               either way (D-FB-11: hiding is never clearing). */
            screens={STEPS
              .flatMap((s) => (s.type === 'screen' ? [{ ...s.screen, groups: askedGroups(s.screen) }] : []))
              .filter((s) => s.groups.length > 0)}
            sectionData={sectionData}
            findFieldSpec={findFieldSpec}
            retiredFields={retiredFieldsBySection}
            retiredShownInEnglish={(path) => retiredInEnglish.has(path)}
            // #2728: the files read back too, slot by slot, under the
            // documents step's own name — a wrong or missing file is caught
            // here instead of by staff after submit.
            documents={(() => {
              const docsStep = STEPS.find((s): s is Extract<StepKind, { type: 'documents' }> => s.type === 'documents');
              return docsStep ? { heading: docsStep.label, slots: docSlots, uploaded: uploadedDocs } : undefined;
            })()}
          />
          <DisclosureGate
            notice={<ConsentEnglishNotice show={uiLocale !== 'en'} className="m-0 text-body-sm leading-relaxed text-stevens-ink-3" />}
            documentId="data_sharing_grant_writing"
            eyebrow={t('forms:wizard.submitConsent.eyebrow')}
            title={t(wordingFor(i18n, flow, 'forms:wizard.submitConsent.title', 'forms:wizard.submitConsent.titleRenewal'))}
            // i18next-instrument-ignore — data-sharing consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language
            explanation={
              'TRF staff and, in some cases, grant-writing partners may access your application ' +
              'to process your request and seek additional funding. ' +
              'Your information is never sold and is only used to support your family.'
            }
            // i18next-instrument-ignore-next-line — data-sharing consent wording stays English and authoritative (R2-B12, R4-B30); the ConsentEnglishNotice says so in the reader’s language
            consentLabel="I understand and agree to TRF using my application as described above."
            onAccepted={() => {
              // Fresh, user-initiated agree → submit.
              handleSubmit();
            }}
            onAlreadyAccepted={() => {
              // Consent was recorded on an earlier visit (e.g. a prior submit
              // that failed). Do NOT auto-resubmit — surface an explicit button.
              setAlreadyConsented(true);
            }}
          />
          {alreadyConsented && (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              variant="brand"
            >
              {saving ? t('forms:wizard.submitting') : t(wordingFor(i18n, flow, 'forms:wizard.submit', 'forms:wizard.submitRenewal'))}
            </Button>
          )}
        </div>
      )}

      {/* ── #1059: address review (corrected/unknown verdicts only) ── */}
      {addressReview && (
        <AddressReviewDialog
          open
          renewal={flow === 'renewal'}
          entered={addressReview.entered}
          result={addressReview.result}
          onUseSuggested={() => addressChoiceRef.current?.('use_suggested')}
          onKeepAsEntered={() => addressChoiceRef.current?.('keep_as_entered')}
          onEditAddress={() => addressChoiceRef.current?.('edit')}
        />
      )}

      {/* ── Navigation buttons ── */}
      {/* Consent steps advance via their own CTA; show Back + Save & continue for everything else */}
      {(step.type === 'screen' || step.type === 'documents') && (
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={stepIndex === 0 || saving || checkingAddress}
            className="text-stevens-ink-3"
          >
            {t('common:actions.back')}
          </Button>
          <Button
            onClick={() => handleSaveAndContinue(step)}
            disabled={saving || checkingAddress}
            variant="brand"
          >
            {saving ? t('forms:wizard.saving') : checkingAddress ? t('forms:wizard.checkingAddress') : t('forms:wizard.saveAndContinue')}
          </Button>
        </div>
      )}

      {/* Back button on consent-only steps */}
      {(step.type === 'consent' || step.type === 'submit_consent') && stepIndex > 0 && (
        <div className="pt-2">
          <Button
            variant="ghost"
            onClick={goBack}
            className="text-stevens-ink-3"
          >
            {t('common:actions.back')}
          </Button>
        </div>
      )}
      </>)}
    </div>
    </div>
    </DateDraftContext.Provider>
  );
}
